import p5 from 'p5'
import '../css/site.sass'
import logoSvgRaw from '../logo.svg?raw'
import slideAddIcon from '../icons/slide-add.svg?raw'
import slideEditIcon from '../icons/slide-edit.svg?raw'
import slideDeleteIcon from '../icons/slide-delete.svg?raw'
import slideCopyIcon from '../icons/slide-copy.svg?raw'
import generateIcon from '../icons/generate.svg?raw'
import exportIcon from '../icons/export.svg?raw'
import sidebarToggleIcon from '../icons/sidebar-toggle.svg?raw'
import overviewIcon from '../icons/overview.svg?raw'

// ── Testbild ───────────────────────────────────────────────────────────────
const TEST_IMAGE = '/resources/medien/fotografien/23_09_27_egp_dji_0428-1500w.webp'

// ── Bilddaten ──────────────────────────────────────────────────────────────

const studie2Urls = Object.values(
  import.meta.glob('../medien/formen/set-2/*.webp', { eager: true, query: '?url', import: 'default' })
)

const bildMaskenUrls = Object.values(
  import.meta.glob('../medien/formen/bild_masken/*.webp', { eager: true, query: '?url', import: 'default' })
)

const fotografieUrls = Object.values(
  import.meta.glob('../medien/fotografien/*.{webp,jpg,jpeg,png,avif}', { eager: true, query: '?url', import: 'default' })
)

const STORAGE_KEY = 'egp-piw-contexts'
const THEME_STORAGE_KEY = 'egp-piw-theme'
const THEME_ICON_MOON = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>'
const THEME_ICON_SUN = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4"/></svg>'
const STORY_SAFE_MARGIN = 250
const STUDIE2_PALETTE = ['#D34634', '#FFBE30', '#309A5C', '#525DD0']
const STUDIE2_NOISE_RATIO = 0.2
const STUDIE2_NOISE_DENSITY_FACTOR = 2000
const CANVAS_FORMATS = {
  instagram: { portrait: [1080, 1350], landscape: [1350, 1080] },
  breitbild: { portrait: [1080, 1920], landscape: [1920, 1080] },
}
const LOGO_ASPECT = 96 / 93
const SUBTITLE_SIZE = 40
const TEXT_LINE_HEIGHT_FACTOR = 1.15
const TEXT_SIZES = { small: 40, medium: 60, large: 80 }
const COLOR_SCHEMES = [
  {
    id: 'schema2',
    name: 'Schema 2',
    pairs: [
      { bg: '#FE832B', text: '#5F2C67' },
      { bg: '#5F2C67', text: '#FE832B' },
      { bg: '#2A7540', text: '#D1C69E' },
      { bg: '#D1C69E', text: '#2A7540' },
      { bg: '#E8D681', text: '#BA4730' },
      { bg: '#BA4730', text: '#E8D681' },
      { bg: '#8A3E27', text: '#72BBE5' },
      { bg: '#72BBE5', text: '#8A3E27' },
      { bg: '#571818', text: '#BAB5A7' },
      { bg: '#BAB5A7', text: '#571818' },
    ]
  }
]

// ── Kontrasthilfen ────────────────────────────────────────────────────────

const hexLuminance = (hex) => {
  const lin = (c) => c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  return 0.2126 * lin(parseInt(hex.slice(1, 3), 16) / 255)
       + 0.7152 * lin(parseInt(hex.slice(3, 5), 16) / 255)
       + 0.0722 * lin(parseInt(hex.slice(5, 7), 16) / 255)
}

const contrastBg = (color, palette) =>
  palette.reduce((best, c) => {
    const l1 = hexLuminance(color), l2 = hexLuminance(c)
    const hi = Math.max(l1, l2), lo = Math.min(l1, l2)
    const ratio = (hi + 0.05) / (lo + 0.05)
    const bHi = Math.max(l1, hexLuminance(best)), bLo = Math.min(l1, hexLuminance(best))
    return ratio > (bHi + 0.05) / (bLo + 0.05) ? c : best
  })

// ── Platzierungslogik ──────────────────────────────────────────────────────

const placeForms = (images, mode, p, options = {}) => {
  if (mode === 'studie2') {
    const numCols = options.columns ?? 6
    const _m = 80
    const cellSize = options.clampSides ? (p.width - 2 * _m) / numCols : p.width / numCols
    let yStart, numRows
    if (options.excludeZones?.length || options.clampSides) {
      const topZone = options.excludeZones?.find(z => z.y === 0)
      const botZone = options.excludeZones?.find(z => z.y > 0)
      const topBound = topZone ? topZone.h : (options.clampSides ? _m : 0)
      const botBound = botZone ? botZone.y : (options.clampSides ? p.height - _m : p.height)
      const availH = botBound - topBound
      numRows = Math.max(1, Math.floor(availH / cellSize))
      yStart = topBound + (availH - numRows * cellSize) / 2
    } else {
      numRows = Math.ceil(p.height / cellSize) + 1
      yStart = -(numRows * cellSize - p.height) / 2
    }
    const xStart = options.clampSides ? _m : -_m
    const totalCols = options.clampSides ? numCols : Math.ceil((p.width + 2 * _m) / cellSize)

    const occupied = Array.from({ length: numRows + 3 }, () => new Array(totalCols).fill(false))

    const fits = (row, col, size) => {
      if (col + size > totalCols || row + size > numRows + 3) return false
      for (let r = row; r < row + size; r++)
        for (let c = col; c < col + size; c++)
          if (occupied[r][c]) return false
      return true
    }

    const occupy = (row, col, size) => {
      for (let r = row; r < row + size; r++)
        for (let c = col; c < col + size; c++)
          occupied[r][c] = true
    }

    const placed = []
    const rightAngles = [0, p.HALF_PI, p.PI, p.PI + p.HALF_PI]
    const bgColor = options.bgColor ?? '#FFFFFF'
    const formPalette = options.formPalette ?? STUDIE2_PALETTE.filter(c => c !== bgColor)

    for (let row = 0; row < numRows; row++) {
      for (let col = 0; col < totalCols; col++) {
        if (occupied[row][col]) continue

        const maxSize = Math.min(3, totalCols - 1)
        const rand = p.random()
        let size = 1
        if (maxSize >= 3 && rand < 0.15 && fits(row, col, 3)) size = 3
        else if (maxSize >= 2 && rand < 0.45 && fits(row, col, 2)) size = 2

        if (options.excludeZones?.length || options.clampSides) {
          const botBound = options.excludeZones?.find(z => z.y > 0)?.y ?? (options.clampSides ? p.height - _m : p.height)
          const maxFit = Math.floor((botBound - yStart - row * cellSize) / cellSize)
          if (maxFit < size) size = Math.max(1, maxFit)
        }

        occupy(row, col, size)

        const img = images[Math.floor(p.random(images.length))]
        const fw = size * cellSize
        const fh = size * cellSize
        const color = formPalette[Math.floor(p.random(formPalette.length))]

        const angle = rightAngles[Math.floor(p.random(4))]

        const drawW = fw
        const drawH = fh

        const g = p.createGraphics(fw, fh)
        g.clear()
        g.push()
        g.translate(fw / 2, fh / 2)
        g.rotate(angle)
        g.image(img, -drawW / 2, -drawH / 2, drawW, drawH)
        g.pop()
        g.drawingContext.globalCompositeOperation = 'source-atop'
        g.noStroke()
        g.fill(color)
        g.rect(0, 0, fw, fh)
        if (bgColor) {
          g.drawingContext.globalCompositeOperation = 'destination-over'
          g.fill(bgColor)
          g.rect(0, 0, fw, fh)
        }
        g.drawingContext.globalCompositeOperation = 'source-over'

        placed.push({ gfx: g, x: xStart + col * cellSize, y: yStart + row * cellSize, img, angle, fw, fh })
      }
    }

    return placed
  }

  // random (default)
  return images.map((img) => ({
    img, x: p.random(p.width), y: p.random(p.height), angle: p.random(p.TWO_PI),
  }))
}

// ── Slide-State-Fabrik ─────────────────────────────────────────────────────

const defaultSlideState = () => ({
  slideId: Math.random().toString(36).slice(2) + Date.now().toString(36),
  canvasText: 'Gemeinsam Neues bauen im Trierer Westen auf der Fläche der ehemaligen Jägerkaserne',
  canvasSubtitle: 'Projekt im Westen',
  canvasFormat: 'instagram',
  canvasOrientation: 'portrait',
  canvasTextColor: '#FFFFFF',
  canvasBgColor: '#000000',
  activeColorSchemeId: 'schema2',
  activePairIndex: 0,
  colorDuoton: true,
  showText: true,
  showImage: true,
  showBackground: true,
  showPattern: true,
  canvasImage: null,
  canvasImageUrl: null,
  canvasImageScale: 45,
  canvasImageRotation: 0,
  canvasImageCropX: 50,
  canvasImageCropY: 50,
  canvasImageMask: null,
  canvasImageMaskUrl: null,
  patternClampSides: false,
  canvasTextAlign: 'left',
  canvasTextSize: 'medium',
  canvasTextHyphens: false,
  canvasTextBalance: true,
  textsSwapped: false,
  showLogo: false,
  storyZone: false,
  canvasLogoImage: null,
  _canvasLogoColorCache: null,
  canvasImageMode: 'mask',
  canvasPatternCorner: 'tr',
  canvasBgMenge: 100,
  bgCornerGrid: [],
  activeContextId: 'studie2',
  slideLocked: false,
  lockedSections: { format: false, text: false, image: false, background: false, pattern: false },
  contextState: {
    studie2: { images: [], forms: [], mode: 'studie2', seed: 0, options: { columns: 6, blur: false, noise: true }, urls: [], lockedOptions: {} },
  },
  // runtime — not serialized
  p5Instance: null,
  p5Canvas: null,
  canvasContainer: null,
  canvasReady: false,
  currentGenerateFn: null,
  _maskGfx: null, _maskGfxKey: null,
  _blurLayer: null, _blurLayerKey: null,
  _bgSortedCache: null, _bgSortedCacheKey: null,
  _bgGridVersion: 0,
  _backgroundPatternBlocked: false,
})

// ── Slide-Array und aktiver Index ──────────────────────────────────────────

let slides = []
let activeSlideIndex = 0
let overviewMode = false
let draggedSlideIndex = null
const getActive = () => slides[activeSlideIndex]

// ── Theme ─────────────────────────────────────────────────────────────────

let currentTheme = 'dark'

const applyTheme = (theme) => {
  currentTheme = theme === 'light' ? 'light' : 'dark'
  document.documentElement.setAttribute('data-theme', currentTheme)
  const themeToggleButton = document.getElementById('themeToggleButton')
  if (themeToggleButton) {
    themeToggleButton.innerHTML = currentTheme === 'dark' ? THEME_ICON_MOON : THEME_ICON_SUN
    themeToggleButton.setAttribute(
      'aria-label',
      currentTheme === 'dark' ? 'Darkmode aktiv, zu Lightmode wechseln' : 'Lightmode aktiv, zu Darkmode wechseln'
    )
    themeToggleButton.dataset.tooltip = currentTheme === 'dark' ? 'Zu Lightmode wechseln' : 'Zu Darkmode wechseln'
  }
  try { localStorage.setItem(THEME_STORAGE_KEY, currentTheme) } catch {}
}

const loadTheme = () => {
  try {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY)
    if (savedTheme === 'light' || savedTheme === 'dark') currentTheme = savedTheme
  } catch {}
  document.documentElement.setAttribute('data-theme', currentTheme)
}

// ── Logo ──────────────────────────────────────────────────────────────────

const updateLogoImage = (slide) => {
  if (!slide.showLogo) { slide.canvasLogoImage = null; slide._canvasLogoColorCache = null; return }
  if (slide._canvasLogoColorCache === slide.canvasTextColor && slide.canvasLogoImage) return
  slide._canvasLogoColorCache = slide.canvasTextColor
  const colored = logoSvgRaw.replace(/currentColor/g, slide.canvasTextColor)
  const blob = new Blob([colored], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(blob)
  const img = new Image()
  img.onload = () => {
    URL.revokeObjectURL(url)
    slide.canvasLogoImage = img
    if (slide.canvasReady) slide.p5Instance.redraw()
  }
  img.onerror = () => URL.revokeObjectURL(url)
  img.src = url
}

const logoFitsInSubtitleLine = (slide) => {
  if (!slide.canvasReady || !slide.canvasSubtitle) return false
  const p = slide.p5Instance
  const _tw = p.width - 2 * 80
  const _logoW = SUBTITLE_SIZE * 1.3 * LOGO_ASPECT
  p.textFont('Social')
  applyTextMetrics(p, SUBTITLE_SIZE)
  return p.textWidth(slide.canvasSubtitle) <= _tw - _logoW - 20
}

// Setzt textFont('Social') neu und misst danach — p5 kann die Font-Auflösung
// pro Canvas fälschlich auf den Fallback festnageln, wenn vor diesem expliziten
// Refresh gemessen wird; ohne das Flag würde ein einmal falsch gemessenes
// "passt nicht" showLogo dauerhaft und unwiderruflich auf false zwingen
const ensureLogoFontRefreshed = (slide) => {
  if (slide._fontRefreshed || !slide.canvasReady) return
  if (!document.fonts.check(`${SUBTITLE_SIZE}px Social`)) return
  slide.p5Instance.textFont('Social')
  slide._fontRefreshed = true
}

const updateLogoCheckState = (slide) => {
  ensureLogoFontRefreshed(slide)
  // Vor dem Font-Refresh misst textWidth den Fallback-Font (breiter) — das Logo
  // würde fälschlich dauerhaft deaktiviert; Re-Check folgt bei fonts.ready
  if (!slide._fontRefreshed) return
  const fits = logoFitsInSubtitleLine(slide)
  if (!fits && slide.showLogo) {
    slide.showLogo = false
    updateLogoImage(slide)
    if (slide.canvasReady) slide.p5Instance.redraw()
  }
  if (slide !== getActive()) return
  const logoCheck = document.getElementById('logoCheck')
  if (!logoCheck) return
  logoCheck.disabled = !fits
  logoCheck.checked = slide.showLogo
}

// ── Farbschema-Hilfsfunktionen ────────────────────────────────────────────

const getActiveScheme = (slide) => COLOR_SCHEMES.find(s => s.id === slide.activeColorSchemeId) ?? COLOR_SCHEMES[0]
const getActivePair = (slide) => { const s = getActiveScheme(slide); return s.pairs[slide.activePairIndex] ?? s.pairs[0] }
const getFormPalette = (slide) => {
  const scheme = getActiveScheme(slide)
  if (slide.colorDuoton) return [getActivePair(slide).text]
  return scheme.pairs.map(p => p.bg).filter(c => c !== slide.canvasBgColor)
}

// ── IndexedDB für hochgeladene Bilder ──────────────────────────────────────
// Blob-URLs überleben kein Reload; die Original-Datei wird deshalb pro Slide
// (Schlüssel: slideId) abgelegt und beim Start wiederhergestellt.

const IDB_NAME = 'egp-piw-images'
const IDB_STORE = 'images'

const idbRequest = (mode, fn) => new Promise((resolve, reject) => {
  const open = indexedDB.open(IDB_NAME, 1)
  open.onupgradeneeded = () => open.result.createObjectStore(IDB_STORE)
  open.onerror = () => reject(open.error)
  open.onsuccess = () => {
    const tx = open.result.transaction(IDB_STORE, mode)
    const req = fn(tx.objectStore(IDB_STORE))
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  }
}).catch(() => undefined)

const idbPutImage = (key, blob) => key ? idbRequest('readwrite', s => s.put(blob, key)) : Promise.resolve()
const idbGetImage = (key) => key ? idbRequest('readonly', s => s.get(key)) : Promise.resolve()
const idbDeleteImage = (key) => key ? idbRequest('readwrite', s => s.delete(key)) : Promise.resolve()

// ── LocalStorage ───────────────────────────────────────────────────────────

const serializeSlide = (slide) => {
  const obj = {
    slideId: slide.slideId,
    canvasText: slide.canvasText,
    canvasSubtitle: slide.canvasSubtitle,
    canvasFormat: slide.canvasFormat,
    canvasOrientation: slide.canvasOrientation,
    activeColorSchemeId: slide.activeColorSchemeId,
    activePairIndex: slide.activePairIndex,
    colorDuoton: slide.colorDuoton,
    canvasImageScale: slide.canvasImageScale,
    canvasImageRotation: slide.canvasImageRotation,
    canvasImageCropX: slide.canvasImageCropX,
    canvasImageCropY: slide.canvasImageCropY,
    patternClampSides: slide.patternClampSides,
    canvasTextAlign: slide.canvasTextAlign,
    canvasTextSize: slide.canvasTextSize,
    canvasTextHyphens: slide.canvasTextHyphens,
    canvasTextBalance: slide.canvasTextBalance,
    textsSwapped: slide.textsSwapped,
    showLogo: slide.showLogo,
    showText: slide.showText,
    showImage: slide.showImage,
    showBackground: slide.showBackground,
    showPattern: slide.showPattern,
    storyZone: slide.storyZone,
    canvasImageMode: slide.canvasImageMode,
    canvasPatternCorner: slide.canvasPatternCorner,
    canvasBgMenge: slide.canvasBgMenge,
    activeContextId: slide.activeContextId,
    slideLocked: slide.slideLocked,
    lockedSections: { ...slide.lockedSections },
    canvasImageUrl: slide.canvasImageUrl && !slide.canvasImageUrl.startsWith('blob:') ? slide.canvasImageUrl : null,
    canvasImageMaskUrl: slide.canvasImageMaskUrl && !slide.canvasImageMaskUrl.startsWith('blob:') ? slide.canvasImageMaskUrl : null,
  }
  for (const [id, state] of Object.entries(slide.contextState)) {
    obj[id] = { mode: state.mode, seed: state.seed, options: state.options, urls: state.urls, lockedOptions: state.lockedOptions ?? {} }
  }
  return obj
}

const applySlideData = (slide, data) => {
  if (!data) return
  if (typeof data.slideId === 'string') slide.slideId = data.slideId
  if (typeof data.canvasText === 'string') slide.canvasText = data.canvasText
  if (typeof data.canvasSubtitle === 'string') slide.canvasSubtitle = data.canvasSubtitle
  if (data.canvasFormat && CANVAS_FORMATS[data.canvasFormat]) slide.canvasFormat = data.canvasFormat
  if (data.canvasOrientation) slide.canvasOrientation = data.canvasOrientation
  if (data.activeColorSchemeId && COLOR_SCHEMES.find(s => s.id === data.activeColorSchemeId)) slide.activeColorSchemeId = data.activeColorSchemeId
  if (typeof data.activePairIndex === 'number') slide.activePairIndex = data.activePairIndex
  if (typeof data.colorDuoton === 'boolean') slide.colorDuoton = data.colorDuoton
  const pair = getActivePair(slide)
  slide.canvasBgColor = pair.bg
  slide.canvasTextColor = pair.text
  if (typeof data.canvasImageScale === 'number') slide.canvasImageScale = data.canvasImageScale
  if (typeof data.canvasImageRotation === 'number') slide.canvasImageRotation = data.canvasImageRotation
  if (typeof data.canvasImageCropX === 'number') slide.canvasImageCropX = Math.max(0, Math.min(100, data.canvasImageCropX))
  if (typeof data.canvasImageCropY === 'number') slide.canvasImageCropY = Math.max(0, Math.min(100, data.canvasImageCropY))
  if (typeof data.patternClampSides === 'boolean') slide.patternClampSides = data.patternClampSides
  if (data.canvasTextAlign) slide.canvasTextAlign = data.canvasTextAlign
  if (data.canvasTextSize && TEXT_SIZES[data.canvasTextSize]) slide.canvasTextSize = data.canvasTextSize
  if (typeof data.canvasTextHyphens === 'boolean') slide.canvasTextHyphens = data.canvasTextHyphens
  if (typeof data.canvasTextBalance === 'boolean') slide.canvasTextBalance = data.canvasTextBalance
  if (typeof data.textsSwapped === 'boolean') slide.textsSwapped = data.textsSwapped
  if (typeof data.showLogo === 'boolean') slide.showLogo = data.showLogo
  if (typeof data.showText === 'boolean') slide.showText = data.showText
  if (typeof data.showImage === 'boolean') slide.showImage = data.showImage
  if (typeof data.showBackground === 'boolean') slide.showBackground = data.showBackground
  if (typeof data.showPattern === 'boolean') slide.showPattern = data.showPattern
  if (typeof data.storyZone === 'boolean') slide.storyZone = data.storyZone
  if (data.canvasImageMode) slide.canvasImageMode = data.canvasImageMode
  if (data.canvasPatternCorner) slide.canvasPatternCorner = data.canvasPatternCorner
  if (typeof data.canvasBgMenge === 'number') slide.canvasBgMenge = data.canvasBgMenge
  if (data.activeContextId && slide.contextState[data.activeContextId]) slide.activeContextId = data.activeContextId
  if (typeof data.slideLocked === 'boolean') slide.slideLocked = data.slideLocked
  if (data.lockedSections && typeof data.lockedSections === 'object') slide.lockedSections = { ...slide.lockedSections, ...data.lockedSections }
  if (typeof data.canvasImageUrl === 'string') slide.canvasImageUrl = data.canvasImageUrl
  if (typeof data.canvasImageMaskUrl === 'string') slide.canvasImageMaskUrl = data.canvasImageMaskUrl
  for (const [id, ctxData] of Object.entries(data)) {
    if (!slide.contextState[id] || typeof ctxData !== 'object' || !ctxData) continue
    slide.contextState[id].mode = ctxData.mode ?? slide.contextState[id].mode
    slide.contextState[id].seed = ctxData.seed ?? 0
    slide.contextState[id].options = { ...slide.contextState[id].options, ...ctxData.options }
    slide.contextState[id].urls = ctxData.urls ?? []
    if (ctxData.lockedOptions) slide.contextState[id].lockedOptions = ctxData.lockedOptions
  }
}

const slugify = (str) =>
  str.toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const buildFilename = (slide, slideNumber) => {
  const now = new Date()
  const date = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}_${String(now.getDate()).padStart(2, '0')}`
  const slug = slugify(slide.canvasSubtitle || 'piw')
  return `${date}-${slug}-${slideNumber}.png`
}

let _saveTimer = null
const writeStateNow = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ activeSlideIndex, slides: slides.map(serializeSlide) }))
  } catch {}
}
const saveState = () => {
  clearTimeout(_saveTimer)
  _saveTimer = setTimeout(() => { _saveTimer = null; writeStateNow() }, 400)
}
const flushSaveState = () => {
  if (_saveTimer === null) return
  clearTimeout(_saveTimer)
  _saveTimer = null
  writeStateNow()
}
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flushSaveState() })
window.addEventListener('pagehide', flushSaveState)

const _pendingApplyMap = new Map()
const scheduleApplyPlacement = (state, slide) => {
  if (_pendingApplyMap.has(slide)) cancelAnimationFrame(_pendingApplyMap.get(slide))
  const id = requestAnimationFrame(() => {
    _pendingApplyMap.delete(slide)
    applyPlacement(state, slide)
  })
  _pendingApplyMap.set(slide, id)
}

// true, wenn Slides aus dem LocalStorage wiederhergestellt wurden — dann beim
// Start NICHT neu randomisieren, sonst ginge der gespeicherte Stand verloren
let restoredFromStorage = false

const loadStoredState = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY))
    if (!saved) {
      slides = [defaultSlideState()]
      return
    }
    restoredFromStorage = true

    if (saved.slides && Array.isArray(saved.slides)) {
      // New multi-slide format
      slides = saved.slides.map(data => {
        const slide = defaultSlideState()
        applySlideData(slide, data)
        return slide
      })
      activeSlideIndex = Math.max(0, Math.min(saved.activeSlideIndex ?? 0, slides.length - 1))
    } else {
      // Old single-slide format — backward compat
      const slide = defaultSlideState()
      applySlideData(slide, saved)
      slides = [slide]
      activeSlideIndex = 0
    }
  } catch {
    slides = [defaultSlideState()]
    activeSlideIndex = 0
  }
  if (!slides.length) slides = [defaultSlideState()]
}

// ── Bilder neu laden (nach Storage-Restore) ────────────────────────────────

const reloadContextImages = (state, slide) => {
  if (!state.urls.length) return
  const images = new Array(state.urls.length).fill(null)
  let loadedCount = 0
  state.urls.forEach((url, i) => {
    slide.p5Instance.loadImage(url, (img) => {
      images[i] = img
      loadedCount++
      if (loadedCount === state.urls.length) {
        state.images = images
        applyPlacement(state, slide)
      }
    })
  })
}

// ── Kern-Operationen ───────────────────────────────────────────────────────

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min
const randPick = (items) => items[Math.floor(Math.random() * items.length)]

const hasContent = (value) => typeof value === 'string' && value.trim().length > 0

const PATTERN_CORNER_FLIP = { tl: 'bl', bl: 'tl', tr: 'br', br: 'tr' }

// Ecken, an denen das Muster (Vollflächig-Modus) keine Textzeile überschneidet.
// Beide Texte aktiv → keine Ecke frei. Nur oben/unten belegt → die gegenüberliegenden zwei Ecken frei.
const getAllowedPatternCorners = (slide) => {
  const subtitleOnTop = !slide.textsSwapped
  const topOccupied = slide.showText && hasContent(subtitleOnTop ? slide.canvasSubtitle : slide.canvasText)
  const bottomOccupied = slide.showText && hasContent(subtitleOnTop ? slide.canvasText : slide.canvasSubtitle)
  if (topOccupied && bottomOccupied) return []
  if (topOccupied) return ['bl', 'br']
  if (bottomOccupied) return ['tl', 'tr']
  return ['tl', 'tr', 'bl', 'br']
}

const syncPatternCornerButtons = (slide, allowedCorners = getAllowedPatternCorners(slide)) => {
  document.querySelectorAll('.pattern-corner-btn').forEach((btn) => {
    const isAllowed = allowedCorners.includes(btn.dataset.patternCorner)
    btn.disabled = !isAllowed
    btn.classList.toggle('active', slide.canvasPatternCorner === btn.dataset.patternCorner)
    btn.title = isAllowed ? '' : 'Diese Ecke überschneidet sich mit dem sichtbaren Text'
  })
}

const syncVisibilityButtons = (slide) => {
  const imageIsMask = Boolean(slide.canvasImage) && slide.showImage && slide.canvasImageMode === 'mask'
  const imageIsBackground = Boolean(slide.canvasImage) && slide.showImage && slide.canvasImageMode === 'background'
  const allowedCorners = getAllowedPatternCorners(slide)
  const backgroundPatternBlocked = imageIsBackground && allowedCorners.length === 0
  const patternBlocked = imageIsMask || backgroundPatternBlocked
  // Sobald der Textkonflikt sich auflöst, Muster wieder automatisch aktivieren (nicht nur beim Moduswechsel)
  if (imageIsBackground && slide._backgroundPatternBlocked && !backgroundPatternBlocked) slide.showPattern = true
  slide._backgroundPatternBlocked = backgroundPatternBlocked
  if (patternBlocked) slide.showPattern = false
  if (imageIsBackground && allowedCorners.length && !allowedCorners.includes(slide.canvasPatternCorner)) {
    const flipped = PATTERN_CORNER_FLIP[slide.canvasPatternCorner]
    slide.canvasPatternCorner = allowedCorners.includes(flipped) ? flipped : allowedCorners[0]
  }
  document.querySelectorAll('.visibility-btn').forEach((btn) => {
    const toggle = btn.dataset.canvasToggle
    if (toggle === 'text') btn.classList.toggle('is-hidden', !slide.showText)
    else if (toggle === 'image') btn.classList.toggle('is-hidden', !slide.showImage)
    else if (toggle === 'background') btn.classList.toggle('is-hidden', !slide.showBackground)
    else if (toggle === 'pattern') {
      btn.classList.toggle('is-hidden', !slide.showPattern)
      btn.disabled = patternBlocked
      btn.title = imageIsMask
        ? 'Muster nicht verfügbar, solange das Bild als Maske eingestellt ist'
        : backgroundPatternBlocked
          ? 'Muster nicht verfügbar, da beide Texte aktiv sind'
          : ''
    }
  })
  syncPatternCornerButtons(slide, allowedCorners)
}

const randomizeOptions = (state, contextId, slide = null) => {
  const locked = state.lockedOptions ?? {}
  if (contextId === 'studie2') {
    const isLandscapeVollformat = slide?.canvasFormat === 'breitbild' && slide?.canvasOrientation === 'landscape'
    if (!locked.columns) state.options.columns = isLandscapeVollformat ? randInt(8, 24) : randInt(3, 8)
  }
}

const applyPlacement = (state, slide) => {
  if (!state.images.length) return
  for (const form of state.forms) { if (form.gfx) form.gfx.remove() }
  slide._blurLayer?.remove(); slide._blurLayer = null; slide._blurLayerKey = null
  slide.p5Instance.randomSeed(state.seed)
  const isStoryZone = slide.storyZone && slide.canvasFormat === 'breitbild' && slide.canvasOrientation === 'portrait'
  const storyZones = isStoryZone ? [
    { x: 0, y: 0, w: slide.p5Instance.width, h: STORY_SAFE_MARGIN },
    { x: 0, y: slide.p5Instance.height - STORY_SAFE_MARGIN, w: slide.p5Instance.width, h: STORY_SAFE_MARGIN },
  ] : []
  const excludeZones = [...computeTextZones(slide.p5Instance, slide), ...storyZones]
  const opts = state.mode === 'studie2'
    ? { ...state.options, bgColor: slide.canvasBgColor, formPalette: getFormPalette(slide), excludeZones, clampSides: slide.patternClampSides }
    : state.options
  state.forms = placeForms(state.images, state.mode, slide.p5Instance, opts)
  if (excludeZones.length && state.mode !== 'studie2') {
    state.forms = state.forms.filter(f => !excludeZones.some(z => f.y >= z.y && f.y <= z.y + z.h))
  }
  if (slide.canvasReady && slide.canvasImage && slide.canvasImageMode === 'background') buildBgCornerGrid(slide)
  if (slide.canvasReady) slide.p5Instance.redraw()
  saveState()
}

const loadStudie2Pattern = (state, slide) => {
  state.seed = Math.floor(Math.random() * 1e6)

  if (state.images.length === studie2Urls.length) {
    applyPlacement(state, slide)
    return
  }

  state.urls = [...studie2Urls]
  saveState()

  const images = new Array(studie2Urls.length).fill(null)
  let loadedCount = 0
  studie2Urls.forEach((url, i) => {
    slide.p5Instance.loadImage(url, (img) => {
      images[i] = img
      loadedCount++
      if (loadedCount === studie2Urls.length) {
        state.images = images
        applyPlacement(state, slide)
      }
    })
  })
}

// ── Text-Messung ───────────────────────────────────────────────────────────

const getWrappedLines = (p, text, maxWidth) => {
  const lines = []
  for (const paragraph of text.split('\n')) {
    if (!paragraph.trim()) { lines.push(''); continue }
    let line = ''
    for (const word of paragraph.split(' ')) {
      const test = line ? `${line} ${word}` : word
      if (p.textWidth(test) > maxWidth && line) { lines.push(line); line = word }
      else line = test
    }
    if (line) lines.push(line)
  }
  return lines.length ? lines : ['']
}

const measureWrappedLines = (p, text, maxWidth) => Math.max(getWrappedLines(p, text, maxWidth).length, 1)

const applyTextMetrics = (p, fontSize) => {
  p.textSize(fontSize)
  p.textLeading(fontSize * TEXT_LINE_HEIGHT_FACTOR)
}

// ── Silbentrennung ────────────────────────────────────────────────────────

const _hyphenCache = { key: null, lines: null }

const getHyphenLines = (text, maxWidth, fontSize, slide) => {
  const key = `${text}|${maxWidth}|${fontSize}|${slide.canvasTextHyphens ? 1 : 0}|${slide.canvasTextBalance ? 1 : 0}`
  if (key === _hyphenCache.key) return _hyphenCache.lines

  const el = document.createElement('div')
  const extraCss = (slide.canvasTextHyphens ? 'hyphens:auto;' : '') + (slide.canvasTextBalance ? 'text-wrap:balance;' : '')
  el.style.cssText = `position:absolute;top:-9999px;left:-9999px;width:${maxWidth}px;font-family:Social;font-size:${fontSize}px;line-height:${TEXT_LINE_HEIGHT_FACTOR};${extraCss}word-break:normal;white-space:normal;overflow-wrap:normal;`
  el.lang = 'de'
  document.body.appendChild(el)

  const isWordChar = (c) => /[\wäöüÄÖÜß]/.test(c)
  const allLines = []

  for (const para of text.split('\n')) {
    if (!para) { allLines.push(''); continue }
    el.textContent = para
    const node = el.firstChild
    if (!node) { allLines.push(''); continue }

    const lines = []
    let line = ''
    let prevTop = null

    for (let i = 0; i < para.length; i++) {
      const range = document.createRange()
      range.setStart(node, i)
      range.setEnd(node, i + 1)
      const top = range.getBoundingClientRect().top

      if (prevTop !== null && top > prevTop + 1) {
        const lastSpace = line.lastIndexOf(' ')
        const wordFrag = lastSpace >= 0 ? line.slice(lastSpace + 1) : line
        const addHyphen = isWordChar(para[i - 1]) && isWordChar(para[i]) && wordFrag.length >= 2
        lines.push(addHyphen ? line + '-' : line.trimEnd())
        line = para[i]
      } else {
        line += para[i]
      }
      prevTop = top
    }
    if (line) lines.push(line.trimEnd())
    allLines.push(...lines)
  }

  document.body.removeChild(el)
  _hyphenCache.key = key
  _hyphenCache.lines = allLines
  return allLines
}

// ── Textzone-Berechnung ────────────────────────────────────────────────────

const rectsOverlap = (ax, ay, aw, ah, bx, by, bw, bh) =>
  ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by

const computeTextZones = (p, slide) => {
  const zones = []
  if (!slide.showText || (!slide.canvasSubtitle && !slide.canvasText)) return zones
  const _m = (slide.storyZone && slide.canvasFormat === 'breitbild' && slide.canvasOrientation === 'portrait') ? STORY_SAFE_MARGIN : 80
  const _gap = 40
  const _tw = p.width - 2 * 80
  p.textFont('Social')
  const _swapped = slide.textsSwapped
  const topContent = _swapped ? slide.canvasText : slide.canvasSubtitle
  const bottomContent = _swapped ? slide.canvasSubtitle : slide.canvasText
  if (topContent) {
    if (_swapped) {
      applyTextMetrics(p, TEXT_SIZES[slide.canvasTextSize])
      const lineCount = (slide.canvasTextHyphens || slide.canvasTextBalance) ? getHyphenLines(slide.canvasText, _tw, TEXT_SIZES[slide.canvasTextSize], slide).length : measureWrappedLines(p, slide.canvasText, _tw)
      zones.push({ x: 0, y: 0, w: p.width, h: _m + lineCount * p.textLeading() + _gap })
    } else {
      applyTextMetrics(p, SUBTITLE_SIZE)
      const _logoReserve = slide.showLogo ? Math.round(SUBTITLE_SIZE * 1.3 * LOGO_ASPECT) + 20 : 0
      zones.push({ x: 0, y: 0, w: p.width, h: _m + measureWrappedLines(p, slide.canvasSubtitle, _tw - _logoReserve) * p.textLeading() + _gap })
    }
  }
  if (bottomContent) {
    if (_swapped) {
      applyTextMetrics(p, SUBTITLE_SIZE)
      const _logoReserve = slide.showLogo ? Math.round(SUBTITLE_SIZE * 1.3 * LOGO_ASPECT) + 20 : 0
      const h = measureWrappedLines(p, slide.canvasSubtitle, _tw - _logoReserve) * p.textLeading()
      zones.push({ x: 0, y: p.height - _m - h - _gap, w: p.width, h: _m + h + _gap })
    } else {
      applyTextMetrics(p, TEXT_SIZES[slide.canvasTextSize])
      const lineCount = (slide.canvasTextHyphens || slide.canvasTextBalance) ? getHyphenLines(slide.canvasText, _tw, TEXT_SIZES[slide.canvasTextSize], slide).length : measureWrappedLines(p, slide.canvasText, _tw)
      const h = lineCount * p.textLeading()
      zones.push({ x: 0, y: p.height - _m - h - _gap, w: p.width, h: _m + h + _gap })
    }
  }
  return zones
}

// Abstand Hintergrund→Text, einstellbar je Schriftgröße (px), unabhängig von Haupttext/Thema
const TEXT_BG_PADS = {
  40: { x: 10, top: 5, bottom: 3 },
  60: { x: 15, top: 0, bottom: 4 },
  80: { x: 15, top: 0, bottom: 4 },
}

// Abstand zwischen 'top'- und 'bottom'-Baseline des Canvas — an diesen Ankern hängt der
// gezeichnete Text (oben TOP-, unten BOTTOM-aligned). Weicht vom leading ab; Boxen müssen
// daran statt am Zeilenraster ankern, sonst verschiebt sich der Abstand beim Text-Tausch.
const measureBaselineSpan = (p) => {
  p.textWidth('Hg') // erzwingt Sync der Font-Properties auf den Canvas-Kontext
  const ctx = p.drawingContext
  const prev = ctx.textBaseline
  ctx.textBaseline = 'top'
  const t = ctx.measureText('Hg').actualBoundingBoxAscent
  ctx.textBaseline = 'bottom'
  const b = ctx.measureText('Hg').actualBoundingBoxAscent
  ctx.textBaseline = prev
  return b - t
}

// Eine Box pro Zeile, genau so breit wie die jeweilige Zeile (statt einer Box über den ganzen Block) –
// bei unterschiedlichen Zeilenlängen ergibt das bewusst eine unregelmäßige statt einer geraden Kante.
const computeTextBgBoxes = (p, slide) => {
  const boxes = []
  if (!slide.showText || (!slide.canvasSubtitle && !slide.canvasText)) return boxes
  const _m = (slide.storyZone && slide.canvasFormat === 'breitbild' && slide.canvasOrientation === 'portrait') ? STORY_SAFE_MARGIN : 80
  const _mh = 80
  const _tw = p.width - 2 * _mh
  const isCenter = slide.canvasTextAlign === 'center'
  p.textFont('Social')
  const _swapped = slide.textsSwapped
  const topContent = _swapped ? slide.canvasText : slide.canvasSubtitle
  const bottomContent = _swapped ? slide.canvasSubtitle : slide.canvasText

  const addLineBoxes = (content, isSubtitleSlot, isTop) => {
    if (!content) return
    const fontSize = isSubtitleSlot ? SUBTITLE_SIZE : TEXT_SIZES[slide.canvasTextSize]
    applyTextMetrics(p, fontSize)
    const pad = TEXT_BG_PADS[fontSize]
    const hasLogo = isSubtitleSlot && slide.showLogo
    const logoH = hasLogo ? fontSize * 1.3 : 0
    const logoW = hasLogo ? logoH * LOGO_ASPECT : 0
    const logoGap = hasLogo ? 20 : 0
    const wrapWidth = _tw - logoW - logoGap
    const leading = p.textLeading()
    const span = measureBaselineSpan(p)
    const useHyphenApi = isSubtitleSlot ? slide.canvasTextBalance : (slide.canvasTextHyphens || slide.canvasTextBalance)

    const lines = (hasLogo && isCenter)
      ? [content]
      : (useHyphenApi ? getHyphenLines(content, wrapWidth, fontSize, slide) : getWrappedLines(p, content, wrapWidth))

    lines.forEach((line, i) => {
      const includeLogo = hasLogo && i === 0
      const lineWidth = (hasLogo && isCenter) ? Math.min(p.textWidth(content), wrapWidth) : p.textWidth(line)
      const lineBlockWidth = includeLogo ? logoW + logoGap + lineWidth : lineWidth
      const x = isCenter
        ? (p.width - lineBlockWidth) / 2
        : _mh + (hasLogo && !includeLogo ? logoW + logoGap : 0)
      // Oberkante der Glyphenzelle: oben hängt der Text am TOP-Anker (_m + i*leading),
      // unten am BOTTOM-Anker (p.height - _m - (n-1-i)*leading), Zellhöhe = span
      const y = isTop
        ? _m + i * leading
        : p.height - _m - (lines.length - 1 - i) * leading - span

      boxes.push({
        x: Math.max(0, x - pad.x),
        y: y - pad.top,
        w: Math.min(p.width, lineBlockWidth + pad.x * 2),
        h: (includeLogo ? Math.max(span, logoH) : span) + pad.top + pad.bottom,
      })
    })
  }

  addLineBoxes(topContent, !_swapped, true)
  addLineBoxes(bottomContent, _swapped, false)

  return boxes
}

// ── UI-Hilfsfunktionen ─────────────────────────────────────────────────────

const mk = (tag, props = {}) => Object.assign(document.createElement(tag), props)

const mkLabel = (text) => mk('label', { textContent: text })

const mkSelect = (options, selectedValue) => {
  const sel = mk('select')
  options.forEach(([value, text]) => {
    const opt = mk('option', { value, textContent: text })
    if (value === selectedValue) opt.selected = true
    sel.appendChild(opt)
  })
  return sel
}

// ── Tooltip (einheitlich für alle Elemente mit data-tooltip/-shortcut) ─────

let _tooltipEl = null
const hideTooltip = () => _tooltipEl?.classList.remove('is-visible')

const initTooltips = () => {
  _tooltipEl = mk('div', { className: 'ui-tooltip' })
  document.body.appendChild(_tooltipEl)
  document.addEventListener('mouseover', (e) => {
    const target = e.target.closest?.('[data-tooltip]')
    if (!target || target.disabled) { hideTooltip(); return }
    _tooltipEl.textContent = target.dataset.tooltip
    if (target.dataset.shortcut) _tooltipEl.appendChild(mk('kbd', { textContent: target.dataset.shortcut }))
    _tooltipEl.classList.add('is-visible')
    const r = target.getBoundingClientRect()
    const tw = _tooltipEl.offsetWidth
    const th = _tooltipEl.offsetHeight
    const x = Math.max(8, Math.min(r.left + r.width / 2 - tw / 2, window.innerWidth - tw - 8))
    const y = r.top - th - 8 > 8 ? r.top - th - 8 : r.bottom + 8
    _tooltipEl.style.left = `${x}px`
    _tooltipEl.style.top = `${y}px`
  })
  // Nach Klick ausblenden — die Aktion kann Zustand/Beschriftung ändern
  document.addEventListener('click', hideTooltip, true)
}

const mkButton = (text, onClick) => {
  const btn = mk('button', { textContent: text })
  btn.addEventListener('click', onClick)
  return btn
}

const GENERATE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 2.1l4 4-4 4"/><path d="M3 12.2v-2a4 4 0 0 1 4-4h12.8M7 21.9l-4-4 4-4"/><path d="M21 11.8v2a4 4 0 0 1-4 4H4.2"/></svg>`
const AUTOPLAY_MS = 10000
const AUTOPLAY_PLAY_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>`
const AUTOPLAY_STOP_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect></svg>`
const LOCK_OPEN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 9.9-1"></path></svg>`
const LOCK_CLOSED_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>`

const mkRangeControl = (labelText, value, min, max, unit, onInput) => {
  const wrapper = mk('div')
  wrapper.className = 'sub-options'

  const row = mk('div')
  row.className = 'slider-row'
  const valueNode = mk('span', { textContent: `${value}${unit}` })
  row.append(mkLabel(labelText), valueNode)

  const input = mk('input')
  Object.assign(input, { type: 'range', min, max, step: 1, value })
  input.addEventListener('input', () => {
    valueNode.textContent = `${input.value}${unit}`
    onInput(parseFloat(input.value))
  })

  wrapper.append(row, input)
  return wrapper
}

const mkSwatches = (colors, selected, onChange) => {
  const row = mk('div'); row.className = 'color-swatches'
  colors.forEach(color => {
    const btn = mk('button')
    btn.className = 'color-swatch' + (color === selected ? ' selected' : '')
    btn.style.setProperty('--swatch-color', color)
    btn.title = color
    btn.addEventListener('click', () => {
      row.querySelectorAll('.color-swatch').forEach(s => s.classList.toggle('selected', s.title === color))
      onChange(color)
    })
    row.appendChild(btn)
  })
  return row
}

const mkLockCheckbox = (state, key) => {
  const box = mk('input')
  Object.assign(box, { type: 'checkbox', checked: state.lockedOptions?.[key] ?? false })
  box.className = 'lock-checkbox'
  box.title = 'Fixieren'
  box.addEventListener('change', () => {
    if (!state.lockedOptions) state.lockedOptions = {}
    state.lockedOptions[key] = box.checked
    saveState()
  })
  return box
}

const addSlider = (container, state, label, key, min, max, unit, slide, lockable = false, withRandBtn = false) => {
  const row = mk('div'); row.className = 'slider-row'
  const val = mk('span', { textContent: `${state.options[key] ?? 0}${unit}` })
  row.append(mkLabel(label), val)
  container.appendChild(row)

  const inputRow = mk('div'); inputRow.className = 'slider-input-row'
  const slider = mk('input')
  Object.assign(slider, { type: 'range', min, max, step: 1, value: state.options[key] ?? 0 })
  slider.addEventListener('input', () => {
    state.options[key] = parseFloat(slider.value)
    val.textContent = `${slider.value}${unit}`
    scheduleApplyPlacement(state, slide)
  })
  inputRow.appendChild(slider)
  if (lockable) inputRow.appendChild(mkLockCheckbox(state, key))
  if (withRandBtn) {
    const randBtn = mk('button', { type: 'button', innerHTML: GENERATE_SVG, title: 'Zufällig' })
    randBtn.className = 'orientation-btn'
    randBtn.addEventListener('click', () => {
      const newVal = Math.floor(Math.random() * (max - min + 1)) + min
      state.options[key] = newVal
      slider.value = newVal
      val.textContent = `${newVal}${unit}`
      applyPlacement(state, slide)
    })
    inputRow.appendChild(randBtn)
  }
  container.appendChild(inputRow)
}

// ── Canvas-Hilfsfunktionen ─────────────────────────────────────────────────

const findLargestForm = (forms, canvasW, canvasH) => {
  const margin = 200
  let largest = null
  let largestArea = -1
  for (const form of forms) {
    let cx, cy, area
    if (form.gfx != null) {
      const fw = form.fw ?? form.gfx.width
      const fh = form.fh ?? form.gfx.height
      cx = form.x + fw / 2; cy = form.y + fh / 2
      area = fw * fh
    } else if (form.img) {
      cx = form.x; cy = form.y
      const s = form.drawScale ?? 1; area = form.img.width * s * form.img.height * s
    } else continue
    if (cx < margin || cx > canvasW - margin || cy < margin || cy > canvasH - margin) continue
    if (area > largestArea) { largestArea = area; largest = form }
  }
  return largest
}

const buildBgCornerGrid = (slide) => {
  slide.bgCornerGrid.forEach(f => { if (f.gfx) f.gfx.remove() })
  slide.bgCornerGrid = []
  slide._bgGridVersion = (slide._bgGridVersion ?? 0) + 1
  slide._bgSortedCache = null
  if (!slide.canvasReady) return
  const activeState = slide.contextState[slide.activeContextId]
  if (!activeState.images.length) return
  const palette = getFormPalette(slide)
  if (!palette.length) return
  const p = slide.p5Instance
  const images = activeState.images
  const numCols = activeState.options.columns ?? 6
  const cellSize = p.width / numCols
  const numRows = Math.ceil(p.height / cellSize)
  const xStart = 0
  const rightAngles = [0, p.HALF_PI, p.PI, p.PI + p.HALF_PI]
  p.randomSeed(activeState.seed + 77777)
  for (let row = 0; row < numRows; row++) {
    for (let col = 0; col < numCols; col++) {
      const img = images[Math.floor(p.random(images.length))]
      const color = palette[Math.floor(p.random(palette.length))]
      const angle = rightAngles[Math.floor(p.random(4))]
      const g = p.createGraphics(cellSize, cellSize)
      g.clear()
      g.push()
      g.translate(cellSize / 2, cellSize / 2)
      g.rotate(angle)
      g.image(img, -cellSize / 2, -cellSize / 2, cellSize, cellSize)
      g.pop()
      g.drawingContext.globalCompositeOperation = 'source-atop'
      g.noStroke()
      g.fill(color)
      g.rect(0, 0, cellSize, cellSize)
      g.drawingContext.globalCompositeOperation = 'source-over'
      slide.bgCornerGrid.push({ gfx: g, x: xStart + col * cellSize, y: row * cellSize, cellSize })
    }
  }
}

// ── Noise-Layer-Cache ─────────────────────────────────────────────────────

const _noiseLayerCache = new Map()

const getOrBuildNoiseLayer = (p, w, h) => {
  const key = `${w}x${h}`
  if (_noiseLayerCache.has(key)) return _noiseLayerCache.get(key)
  const g = p.createGraphics(w, h)
  g.clear()
  g.noStroke()
  const noiseAlpha = Math.min(60, Math.round(100 * STUDIE2_NOISE_RATIO))
  g.fill(255, noiseAlpha)
  const density = Math.round(100 * STUDIE2_NOISE_DENSITY_FACTOR)
  // Seeded statt Math.random, damit das Korn nach einem Reload identisch ist
  let _s = 987654321
  const rnd = () => (_s = (_s * 1664525 + 1013904223) >>> 0) / 4294967296
  for (let i = 0; i < density; i++) {
    g.rect(rnd() * w, rnd() * h, 1, 1)
  }
  _noiseLayerCache.set(key, g)
  return g
}

// ── Bild-Hilfsfunktionen ──────────────────────────────────────────────────

const pickRandomMask = (slide) => {
  const pool = bildMaskenUrls.length > 0 ? bildMaskenUrls : studie2Urls
  const url = pool[Math.floor(Math.random() * pool.length)]
  slide.canvasImageMaskUrl = url
  slide.canvasImageMask = null // altes Maskenbild sofort verwerfen, bis das neue geladen ist
  saveState()
  slide.p5Instance.loadImage(url, (img) => {
    slide.canvasImageMask = img
    if (slide.canvasReady) slide.p5Instance.redraw()
  })
}

const pickRandomFotografie = (slide) => {
  if (!fotografieUrls.length) return
  const url = fotografieUrls[Math.floor(Math.random() * fotografieUrls.length)]
  slide.p5Instance.loadImage(url, (img) => {
    slide.canvasImageUrl = url
    slide.canvasImage = img
    // canvasImageUrl entsteht erst hier (async) — ohne Save landet die alte URL im Storage
    saveState()
    syncVisibilityButtons(slide)
    if (slide === getActive()) renderCanvasImageControls(slide)
    if (slide.canvasReady) slide.p5Instance.redraw()
  })
}

// ── Bild-Controls-Renderer ────────────────────────────────────────────────

// Ausschnitt-Regler entlang der Achse, auf der das Bild über den Zielausschnitt hinausragt
const appendCropControl = (container, slide, horizontal) => {
  const [label, prop] = horizontal ? ['Ausschnitt X', 'canvasImageCropX'] : ['Ausschnitt Y', 'canvasImageCropY']
  container.appendChild(mkRangeControl(label, slide[prop], 0, 100, '%', (value) => {
    slide[prop] = value
    saveState()
    if (slide.canvasReady) slide.p5Instance.redraw()
  }))
}

const renderCanvasImageControls = (slide) => {
  const imageControls = document.getElementById('canvasImageControls')
  if (!imageControls) return

  imageControls.innerHTML = ''

  // Menge des Muster-Overlays lebt im Reiter „Muster und Effekte", greift aber nur im Vollflächig-Modus
  const bgMengeControl = document.getElementById('bgMengeControl')
  if (bgMengeControl) {
    bgMengeControl.innerHTML = ''
    if (slide.canvasImage && slide.canvasImageMode === 'background') {
      bgMengeControl.appendChild(mkRangeControl('Menge', slide.canvasBgMenge, 10, 100, '%', (value) => {
        slide.canvasBgMenge = value
        saveState()
        if (slide.canvasReady) slide.p5Instance.redraw()
      }))
    }
  }

  if (!slide.canvasImage) return

  const modeRow = mk('div')
  modeRow.className = 'color-scheme-tabs'
  const imageModes = [['mask', 'Maske'], ['background', 'Vollflächig'], ['pattern', 'Muster']]
  imageModes.forEach(([val, label]) => {
    const btn = mk('button', { type: 'button', textContent: label })
    btn.className = 'color-scheme-tab' + (slide.canvasImageMode === val ? ' active' : '')
    btn.addEventListener('click', () => {
      slide.canvasImageMode = val
      if (val === 'pattern' || val === 'background') slide.showPattern = true
      saveState()
      syncVisibilityButtons(slide)
      if (slide === getActive()) renderCanvasImageControls(slide)
      if (slide.canvasReady && val === 'background') buildBgCornerGrid(slide)
      if (slide.canvasReady) slide.p5Instance.redraw()
    })
    modeRow.appendChild(btn)
  })
  imageControls.appendChild(modeRow)

  if (slide.canvasImageMode === 'background') {
    const cornerRow = mk('div')
    cornerRow.className = 'color-scheme-tabs'
    cornerRow.style.display = 'grid'
    cornerRow.style.gridTemplateColumns = '1fr 1fr'
    const corners = [['tl', '↖'], ['tr', '↗'], ['bl', '↙'], ['br', '↘']]
    const allowedCorners = getAllowedPatternCorners(slide)
    corners.forEach(([val, icon]) => {
      const btn = mk('button', { type: 'button', textContent: icon })
      const isAllowed = allowedCorners.includes(val)
      btn.className = 'color-scheme-tab pattern-corner-btn' + (slide.canvasPatternCorner === val ? ' active' : '')
      btn.dataset.patternCorner = val
      btn.disabled = !isAllowed
      btn.title = isAllowed ? '' : 'Diese Ecke überschneidet sich mit dem sichtbaren Text'
      btn.addEventListener('click', () => {
        slide.canvasPatternCorner = val
        saveState()
        if (slide === getActive()) renderCanvasImageControls(slide)
        if (slide.canvasReady) slide.p5Instance.redraw()
      })
      cornerRow.appendChild(btn)
    })
    imageControls.appendChild(cornerRow)
  }

  if (slide.canvasImageMode === 'mask') {
    const imageScaleControl = mkRangeControl('Bildgröße', slide.canvasImageScale, 0, 100, '%', (value) => {
      slide.canvasImageScale = value
      saveState()
      if (slide.canvasReady) slide.p5Instance.redraw()
    })
    imageControls.appendChild(imageScaleControl)

    const imageRotationControl = mkRangeControl('Rotation', slide.canvasImageRotation, -8, 8, '°', (value) => {
      slide.canvasImageRotation = value
      saveState()
      if (slide.canvasReady) slide.p5Instance.redraw()
    })
    imageControls.appendChild(imageRotationControl)
  }

  const _imgAspect = slide.canvasImage.width / slide.canvasImage.height
  if (slide.canvasImageMode === 'background') {
    const [cw, ch] = CANVAS_FORMATS[slide.canvasFormat][slide.canvasOrientation]
    appendCropControl(imageControls, slide, _imgAspect >= cw / ch)
  } else {
    appendCropControl(imageControls, slide, _imgAspect >= 1)
  }

  if (slide.canvasImageMode !== 'background') {
    imageControls.appendChild(mkButton('Neue Maske', () => pickRandomMask(slide)))
  }

  const removeButton = mkButton('Bild entfernen', () => {
    if (slide.canvasImageUrl) URL.revokeObjectURL(slide.canvasImageUrl)
    idbDeleteImage(slide.slideId)
    slide.canvasImageUrl = null
    slide.canvasImage = null
    slide.canvasImageMask = null
    slide.canvasImageMaskUrl = null
    slide.canvasImageMode = 'mask'
    saveState()
    syncVisibilityButtons(slide)
    if (slide === getActive()) {
      const canvasImageUpload = document.getElementById('canvasImageUpload')
      if (canvasImageUpload) canvasImageUpload.value = ''
      renderCanvasImageControls(slide)
    }
    if (slide.canvasReady) slide.p5Instance.redraw()
  })
  imageControls.appendChild(removeButton)
}

// ── Farbschema-UI ─────────────────────────────────────────────────────────

const renderColorSection = (slide) => {
  const container = document.getElementById('colorSchemeControl')
  if (!container) return
  container.innerHTML = ''

  const swatchRow = mk('div')
  swatchRow.className = 'color-pair-swatches'
  const scheme = getActiveScheme(slide)
  scheme.pairs.forEach((pair, idx) => {
    const btn = mk('button')
    btn.type = 'button'
    btn.className = 'color-pair-btn' + (idx === slide.activePairIndex ? ' selected' : '')
    btn.title = `Hintergrund ${pair.bg} / Schrift ${pair.text}`
    btn.style.setProperty('--pair-bg', pair.bg)
    btn.style.setProperty('--pair-text', pair.text)
    btn.textContent = 'Aa'
    btn.addEventListener('click', () => {
      slide.activePairIndex = idx
      slide.canvasBgColor = pair.bg
      slide.canvasTextColor = pair.text
      saveState()
      swatchRow.querySelectorAll('.color-pair-btn').forEach((b, i) => b.classList.toggle('selected', i === idx))
      if (!slide.canvasReady) return
      const s2 = slide.contextState['studie2']
      if (s2.images.length) applyPlacement(s2, slide)
      else slide.p5Instance.redraw()
    })
    swatchRow.appendChild(btn)
  })
  container.appendChild(swatchRow)

  const duotonRow = mk('div'); duotonRow.className = 'slider-row'
  const duotonCheck = mk('input')
  Object.assign(duotonCheck, { type: 'checkbox', id: 'colorDuotonCheck', checked: slide.colorDuoton })
  duotonCheck.addEventListener('change', () => {
    slide.colorDuoton = duotonCheck.checked
    saveState()
    const s2 = slide.contextState['studie2']
    if (slide.canvasReady && s2.images.length) applyPlacement(s2, slide)
    else if (slide.canvasReady) { buildBgCornerGrid(slide); slide.p5Instance.redraw() }
  })
  duotonRow.append(mkLabel('Duoton'), duotonCheck)
  container.appendChild(duotonRow)

  if (slides.length > 1) {
    const syncBtn = mk('button', { type: 'button', textContent: 'Farben auf alle Slides übertragen', className: 'sync-color-btn' })
    syncBtn.addEventListener('click', () => {
      slides.forEach(s => {
        if (s === slide) return
        s.activePairIndex = slide.activePairIndex
        s.canvasBgColor = slide.canvasBgColor
        s.canvasTextColor = slide.canvasTextColor
        s.colorDuoton = slide.colorDuoton
      })
      saveState()
      slides.forEach(s => {
        if (s === slide || !s.canvasReady) return
        const s2 = s.contextState['studie2']
        if (s2?.images?.length) applyPlacement(s2, s)
        else s.p5Instance?.redraw()
      })
    })
    container.appendChild(syncBtn)
  }
}

// ── Kontext-Renderer ───────────────────────────────────────────────────────

const renderStudie2Controls = (container, state, slide) => {
  state.mode = 'studie2'
  const isLandscapeVollformat = slide.canvasFormat === 'breitbild' && slide.canvasOrientation === 'landscape'
  addSlider(container, state, 'Spalten', 'columns', isLandscapeVollformat ? 8 : 3, isLandscapeVollformat ? 24 : 8, '', slide, false, true)

  const blurRow = mk('div'); blurRow.className = 'slider-row'
  const blurCheck = mk('input')
  Object.assign(blurCheck, { type: 'checkbox', checked: state.options.blur ?? false })
  blurCheck.addEventListener('change', () => {
    state.options.blur = blurCheck.checked
    if (slide.canvasReady) slide.p5Instance.redraw()
    saveState()
  })
  blurRow.append(mkLabel('Blur'), blurCheck)
  container.appendChild(blurRow)

  state.options.noise = true

  slide.currentGenerateFn = () => loadStudie2Pattern(state, slide)
  addFormenGenerierenButton(container, slide)
}

const addFormenGenerierenButton = (container, slide) => {
  const btn = mk('button', { type: 'button', innerHTML: `${GENERATE_SVG} Formen generieren` })
  btn.addEventListener('click', () => {
    const s = slide.contextState[slide.activeContextId]
    s.seed = Math.floor(Math.random() * 1e6)
    applyPlacement(s, slide)
    saveState()
  })
  container.appendChild(btn)
}

const contextRenderers = { studie2: renderStudie2Controls }

// ── p5-Sketch-Fabrik ───────────────────────────────────────────────────────

const createSketch = (slide) => (p) => {
  p.preload = () => {}

  p.setup = () => {
    slide.p5Instance = p
    const [cw, ch] = CANVAS_FORMATS[slide.canvasFormat][slide.canvasOrientation]
    slide.p5Canvas = p.createCanvas(cw, ch)
    slide.p5Canvas.parent(slide.canvasContainer)
    // Frisch erstellte und per resizeCanvas angepasste Canvases rastern Text
    // subtil unterschiedlich; einmaliges Resize normalisiert den Kontext, damit
    // ein wiederhergestellter Slide pixelidentisch zur Vor-Reload-Session rendert
    p.resizeCanvas(cw, ch)
    p.frameRate(30)
    p.smooth()
    p.noLoop()
    slide.canvasReady = true
    updateLogoCheckState(slide)
    if (slide._needsRandomize) {
      slide._needsRandomize = false
      randomizeSlide(slide, true)
      return
    }
    for (const state of Object.values(slide.contextState)) reloadContextImages(state, slide)
    if (slide.showLogo) updateLogoImage(slide)
    if (slide.canvasImageUrl) {
      p.loadImage(slide.canvasImageUrl, (img) => {
        slide.canvasImage = img
        if (slide.canvasReady) p.redraw()
      })
    }
    if (slide.canvasImageMaskUrl) {
      p.loadImage(slide.canvasImageMaskUrl, (img) => {
        slide.canvasImageMask = img
        if (slide.canvasReady) p.redraw()
      })
    }
  }

  p.draw = () => {
    if (slide.showBackground) {
      p.background(slide.canvasBgColor)
    } else {
      p.clear()
    }

    const activeState = slide.contextState[slide.activeContextId]

    if (slide.canvasImage && slide.showImage && slide.canvasImageMode === 'background') {
      const aspect = slide.canvasImage.width / slide.canvasImage.height
      const ca = p.width / p.height
      let sx, sy, sw, sh
      if (aspect >= ca) {
        sh = slide.canvasImage.height
        sw = sh * ca
        sy = 0
        sx = Math.max(0, slide.canvasImage.width - sw) * (slide.canvasImageCropX / 100)
      } else {
        sw = slide.canvasImage.width
        sh = sw / ca
        sx = 0
        sy = Math.max(0, slide.canvasImage.height - sh) * (slide.canvasImageCropY / 100)
      }
      p.push()
      p.imageMode(p.CORNER)
      p.image(slide.canvasImage, 0, 0, p.width, p.height, sx, sy, sw, sh)
      p.pop()
    }

    const imageIsMask = slide.canvasImage && slide.showImage && slide.canvasImageMode === 'mask'
    const isBackgroundMode = slide.canvasImage && slide.showImage && slide.canvasImageMode === 'background'
    const backgroundPatternBlocked = isBackgroundMode && getAllowedPatternCorners(slide).length === 0
    if (slide.showPattern && !imageIsMask && !backgroundPatternBlocked) {
      if (slide.canvasImage && slide.showImage && slide.canvasImageMode === 'background') {
        const isRight = slide.canvasPatternCorner.includes('r')
        const isBottom = slide.canvasPatternCorner.includes('b')
        const cornerX = isRight ? p.width : 0
        const cornerY = isBottom ? p.height : 0
        const _bgKey = `${slide.canvasPatternCorner}|${slide._bgGridVersion}`
        if (slide._bgSortedCacheKey !== _bgKey) {
          slide._bgSortedCache = [...slide.bgCornerGrid].sort((a, b) => {
            const acx = a.x + a.cellSize / 2; const acy = a.y + a.cellSize / 2
            const bcx = b.x + b.cellSize / 2; const bcy = b.y + b.cellSize / 2
            return Math.hypot(acx - cornerX, acy - cornerY) - Math.hypot(bcx - cornerX, bcy - cornerY)
          }).filter(f => f.x >= 0 && f.y >= 0 && f.x + f.cellSize <= p.width && f.y + f.cellSize <= p.height)
          slide._bgSortedCacheKey = _bgKey
        }
        const textZones = computeTextZones(p, slide)
        const onCanvas = textZones.length
          ? slide._bgSortedCache.filter(f => !textZones.some(z => rectsOverlap(f.x, f.y, f.cellSize, f.cellSize, z.x, z.y, z.w, z.h)))
          : slide._bgSortedCache
        const n = Math.max(1, Math.round(slide.canvasBgMenge / 100 * onCanvas.length))
        for (const form of onCanvas.slice(0, n)) {
          p.image(form.gfx, form.x, form.y)
        }
      } else {
        p.push()

        const blurPx = (activeState.mode === 'studie2' && activeState.options.blur) ? 200 : 0

        const largestForm = (slide.canvasImage && slide.showImage && slide.canvasImageMode === 'pattern')
          ? findLargestForm(activeState.forms, p.width, p.height)
          : null

        const renderImageInPattern = (target, form) => {
          if (form.gfx != null) {
            const fw = form.fw ?? form.gfx.width
            const fh = form.fh ?? form.gfx.height
            const g = p.createGraphics(fw, fh)
            g.clear()
            const asp = slide.canvasImage.width / slide.canvasImage.height
            const formAsp = fw / fh
            let sx, sy, sw, sh
            if (formAsp > asp) {
              sw = slide.canvasImage.width
              sh = sw / formAsp
              sx = 0
              sy = Math.max(0, slide.canvasImage.height - sh) * (slide.canvasImageCropY / 100)
            } else {
              sh = slide.canvasImage.height
              sw = sh * formAsp
              sy = 0
              sx = Math.max(0, slide.canvasImage.width - sw) * (slide.canvasImageCropX / 100)
            }
            g.imageMode(p.CORNER)
            g.image(slide.canvasImage, 0, 0, fw, fh, sx, sy, sw, sh)
            const maskSrc = slide.canvasImageMask ?? form.img
            if (maskSrc) {
              const maskBuf = p.createGraphics(fw, fh)
              maskBuf.clear()
              maskBuf.push()
              maskBuf.translate(fw / 2, fh / 2)
              if (form.angle != null) maskBuf.rotate(form.angle)
              maskBuf.imageMode(p.CENTER)
              maskBuf.image(maskSrc, 0, 0, fw, fh)
              maskBuf.pop()
              g.drawingContext.globalCompositeOperation = 'destination-in'
              g.imageMode(p.CORNER)
              g.image(maskBuf, 0, 0)
              g.drawingContext.globalCompositeOperation = 'source-over'
              maskBuf.remove()
            }
            target.image(g, form.x, form.y)
            g.remove()
          } else {
            const s = form.drawScale ?? 1
            const nW = form.img.width * s
            const nH = form.img.height * s
            const diag = Math.ceil(Math.sqrt(nW * nW + nH * nH))
            const g = p.createGraphics(diag, diag)
            g.clear()
            const asp = slide.canvasImage.width / slide.canvasImage.height
            const dCw = asp >= 1 ? diag * asp : diag
            const dCh = asp >= 1 ? diag : diag / asp
            g.imageMode(p.CENTER)
            g.image(slide.canvasImage, diag / 2, diag / 2, dCw, dCh)
            const maskBuf = p.createGraphics(diag, diag)
            maskBuf.clear()
            maskBuf.push()
            maskBuf.translate(diag / 2, diag / 2)
            maskBuf.rotate(form.angle)
            maskBuf.imageMode(p.CENTER)
            maskBuf.image(slide.canvasImageMask ?? form.img, 0, 0, nW, nH)
            maskBuf.pop()
            g.drawingContext.globalCompositeOperation = 'destination-in'
            g.imageMode(p.CORNER)
            g.image(maskBuf, 0, 0)
            g.drawingContext.globalCompositeOperation = 'source-over'
            maskBuf.remove()
            target.push()
            target.translate(form.x, form.y)
            target.imageMode(p.CENTER)
            target.image(g, 0, 0)
            target.pop()
            g.remove()
          }
        }

        const renderForms = (target) => {
          for (const form of activeState.forms) {
            if (form === largestForm) { renderImageInPattern(target, form); continue }
            if (form.gfx != null) {
              target.image(form.gfx, form.x, form.y)
            } else {
              target.push()
              target.translate(form.x, form.y)
              target.rotate(form.angle)
              if (form.drawScale != null) target.scale(form.drawScale)
              target.imageMode(p.CENTER)
              target.image(form.img, 0, 0)
              target.pop()
            }
          }
        }

        if (blurPx > 0) {
          const blurKey = `${activeState.seed}|${p.width}x${p.height}`
          if (slide._blurLayerKey !== blurKey) {
            slide._blurLayer?.remove()
            const layer = p.createGraphics(p.width, p.height)
            layer.clear()
            renderForms(layer)
            layer.filter(p.BLUR, Math.max(1, blurPx / 1.5))
            slide._blurLayer = layer
            slide._blurLayerKey = blurKey
          }
          p.image(slide._blurLayer, 0, 0)
        } else {
          renderForms(p)
        }

        p.pop()
      }
    }

    if (Boolean(activeState.options.noise)) {
      p.image(getOrBuildNoiseLayer(p, p.width, p.height), 0, 0)
    }

    const isStoryZone = slide.storyZone && slide.canvasFormat === 'breitbild' && slide.canvasOrientation === 'portrait'
    const _m = isStoryZone ? STORY_SAFE_MARGIN : 80
    const _mh = 80
    const _tw = p.width - 2 * _mh

    p.textFont('Social')
    let topBound = _m
    let bottomBound = p.height - _m

    if (slide.showText) {
      const _swapped = slide.textsSwapped
      if ((_swapped ? slide.canvasText : slide.canvasSubtitle)) {
        if (_swapped) {
          applyTextMetrics(p, TEXT_SIZES[slide.canvasTextSize])
          const _lineCount = (slide.canvasTextHyphens || slide.canvasTextBalance) ? getHyphenLines(slide.canvasText, _tw, TEXT_SIZES[slide.canvasTextSize], slide).length : measureWrappedLines(p, slide.canvasText, _tw)
          topBound = _m + _lineCount * p.textLeading()
        } else {
          applyTextMetrics(p, SUBTITLE_SIZE)
          const _logoReserve = slide.showLogo ? Math.round(SUBTITLE_SIZE * 1.3 * LOGO_ASPECT) + 20 : 0
          topBound = _m + measureWrappedLines(p, slide.canvasSubtitle, _tw - _logoReserve) * p.textLeading()
        }
      }
      if ((_swapped ? slide.canvasSubtitle : slide.canvasText)) {
        if (_swapped) {
          applyTextMetrics(p, SUBTITLE_SIZE)
          const _logoReserve = slide.showLogo ? Math.round(SUBTITLE_SIZE * 1.3 * LOGO_ASPECT) + 20 : 0
          bottomBound = p.height - _m - measureWrappedLines(p, slide.canvasSubtitle, _tw - _logoReserve) * p.textLeading()
        } else {
          applyTextMetrics(p, TEXT_SIZES[slide.canvasTextSize])
          const _lineCount = (slide.canvasTextHyphens || slide.canvasTextBalance) ? getHyphenLines(slide.canvasText, _tw, TEXT_SIZES[slide.canvasTextSize], slide).length : measureWrappedLines(p, slide.canvasText, _tw)
          bottomBound = p.height - _m - _lineCount * p.textLeading()
        }
      }
    }

    if (slide.canvasImage && slide.showImage && slide.canvasImageMode === 'mask') {
      p.push()
      const availableH = Math.max(0, bottomBound - topBound)
      const minH = availableH * 0.618
      const maxH = availableH - 40
      const targetH = minH + (maxH - minH) * (slide.canvasImageScale / 100)
      const size = Math.min(Math.max(targetH, 0), _tw)

      if (size > 0) {
        // canvasImageMask lädt async: Lade-Status muss in den Key, sonst bleibt
        // eine ohne Maske gebaute Grafik dauerhaft im Cache hängen
        const maskKey = `${size.toFixed(1)}|${slide.canvasImageCropX}|${slide.canvasImageCropY}|${slide.canvasImageUrl ?? ''}|${slide.canvasImageMaskUrl ?? ''}|${slide.canvasImageMask ? 1 : 0}`
        if (slide._maskGfxKey !== maskKey) {
          slide._maskGfx?.remove()
          const g = p.createGraphics(size, size)
          g.clear()
          const imgAspect = slide.canvasImage.width / slide.canvasImage.height
          let sx = 0, sy = 0, sw, sh
          if (imgAspect >= 1) {
            sw = slide.canvasImage.height; sh = slide.canvasImage.height
            sx = Math.max(0, slide.canvasImage.width - sw) * (slide.canvasImageCropX / 100)
          } else {
            sw = slide.canvasImage.width; sh = slide.canvasImage.width
            sy = Math.max(0, slide.canvasImage.height - sh) * (slide.canvasImageCropY / 100)
          }
          g.imageMode(p.CORNER)
          g.image(slide.canvasImage, 0, 0, size, size, sx, sy, sw, sh)
          if (slide.canvasImageMask) {
            g.drawingContext.globalCompositeOperation = 'destination-in'
            g.imageMode(p.CORNER)
            g.image(slide.canvasImageMask, 0, 0, size, size)
          }
          slide._maskGfx = g
          slide._maskGfxKey = maskKey
        }
        const cx = p.width / 2
        const cy = (topBound + bottomBound) / 2
        p.push()
        p.translate(cx, cy)
        p.rotate(slide.canvasImageRotation * Math.PI / 180)
        p.imageMode(p.CENTER)
        p.image(slide._maskGfx, 0, 0)
        p.pop()
      }
      p.pop()
    }

    if (slide.showText && slide.canvasImage && slide.showImage && slide.canvasImageMode === 'background') {
      const textBgBoxes = computeTextBgBoxes(p, slide)
      if (textBgBoxes.length) {
        p.push()
        p.noStroke()
        p.fill(slide.canvasBgColor)
        for (const box of textBgBoxes) p.rect(box.x, box.y, box.w, box.h)
        p.pop()
      }
    }

    if (slide.showText) {
      p.fill(slide.canvasTextColor)
      p.noStroke()
      const _swapped = slide.textsSwapped

      // ── Subtitle (Thema) ──────────────────────────────────────────────────
      if (slide.canvasSubtitle) {
        applyTextMetrics(p, SUBTITLE_SIZE)
        if (slide.showLogo && slide._canvasLogoColorCache !== slide.canvasTextColor) updateLogoImage(slide)
        const _logoH = SUBTITLE_SIZE * 1.3
        const _logoW = _logoH * LOGO_ASPECT
        const _logoGap = 20
        const _logoNudge = 4
        const _textTw = _tw - (slide.showLogo && slide.canvasLogoImage ? _logoW + _logoGap : 0)
        const _hAlign = slide.canvasTextAlign === 'center' ? p.CENTER : p.LEFT
        if (!_swapped) {
          // subtitle at top
          if (slide.showLogo && slide.canvasLogoImage) {
            const _logoY = _m + (SUBTITLE_SIZE - _logoH) / 2 + _logoNudge
            if (slide.canvasTextAlign === 'center') {
              const _subtitleW = Math.min(p.textWidth(slide.canvasSubtitle), _textTw)
              const _blockX = (p.width - (_logoW + _logoGap + _subtitleW)) / 2
              p.drawingContext.drawImage(slide.canvasLogoImage, _blockX, _logoY, _logoW, _logoH)
              p.textAlign(p.LEFT, p.TOP)
              p.text(slide.canvasSubtitle, _blockX + _logoW + _logoGap, _m, _textTw)
            } else {
              p.drawingContext.drawImage(slide.canvasLogoImage, _mh, _logoY, _logoW, _logoH)
              p.textAlign(p.LEFT, p.TOP)
              if (slide.canvasTextBalance) {
                const lines = getHyphenLines(slide.canvasSubtitle, _textTw, SUBTITLE_SIZE, slide)
                const leading = p.textLeading()
                lines.forEach((ln, i) => p.text(ln, _mh + _logoW + _logoGap, _m + i * leading))
              } else {
                p.text(slide.canvasSubtitle, _mh + _logoW + _logoGap, _m, _textTw)
              }
            }
          } else {
            p.textAlign(_hAlign, p.TOP)
            const _xBase = slide.canvasTextAlign === 'center' ? p.width / 2 : _mh
            if (slide.canvasTextBalance) {
              const lines = getHyphenLines(slide.canvasSubtitle, _tw, SUBTITLE_SIZE, slide)
              const leading = p.textLeading()
              lines.forEach((ln, i) => p.text(ln, _xBase, _m + i * leading))
            } else {
              p.text(slide.canvasSubtitle, _mh, _m, _tw)
            }
          }
        } else {
          // subtitle at bottom
          if (slide.showLogo && slide.canvasLogoImage) {
            const _logoY = p.height - _m - SUBTITLE_SIZE + (SUBTITLE_SIZE - _logoH) / 2 - _logoNudge * 1.6
            if (slide.canvasTextAlign === 'center') {
              const _subtitleW = Math.min(p.textWidth(slide.canvasSubtitle), _textTw)
              const _blockX = (p.width - (_logoW + _logoGap + _subtitleW)) / 2
              p.drawingContext.drawImage(slide.canvasLogoImage, _blockX, _logoY, _logoW, _logoH)
              p.textAlign(p.LEFT, p.BOTTOM)
              p.text(slide.canvasSubtitle, _blockX + _logoW + _logoGap, p.height - _m, _textTw)
            } else {
              p.drawingContext.drawImage(slide.canvasLogoImage, _mh, _logoY, _logoW, _logoH)
              p.textAlign(p.LEFT, p.BOTTOM)
              if (slide.canvasTextBalance) {
                const lines = getHyphenLines(slide.canvasSubtitle, _textTw, SUBTITLE_SIZE, slide)
                const leading = p.textLeading()
                lines.forEach((ln, i) => p.text(ln, _mh + _logoW + _logoGap, p.height - _m - (lines.length - 1 - i) * leading))
              } else {
                p.text(slide.canvasSubtitle, _mh + _logoW + _logoGap, p.height - _m, _textTw)
              }
            }
          } else {
            p.textAlign(_hAlign, p.BOTTOM)
            const _xBase = slide.canvasTextAlign === 'center' ? p.width / 2 : _mh
            if (slide.canvasTextBalance) {
              const lines = getHyphenLines(slide.canvasSubtitle, _tw, SUBTITLE_SIZE, slide)
              const leading = p.textLeading()
              lines.forEach((ln, i) => p.text(ln, _xBase, p.height - _m - (lines.length - 1 - i) * leading))
            } else {
              p.text(slide.canvasSubtitle, _mh, _m, _tw, p.height - 2 * _m)
            }
          }
        }
      }

      // ── Haupttext ─────────────────────────────────────────────────────────
      if (slide.canvasText) {
        applyTextMetrics(p, TEXT_SIZES[slide.canvasTextSize])
        const _hAlign = slide.canvasTextAlign === 'center' ? p.CENTER : p.LEFT
        const xBase = slide.canvasTextAlign === 'center' ? p.width / 2 : _mh
        const _vertCenter = slide.canvasTextAlign === 'center' && (!slide.canvasImage || !slide.showImage) && !slide.showPattern
        // Available area for vertical centering: between subtitle boundary and canvas edge
        const _areaTop = _swapped ? _m : topBound
        const _areaBottom = _swapped ? bottomBound : p.height - _m

        if (slide.canvasTextHyphens || slide.canvasTextBalance) {
          const lines = getHyphenLines(slide.canvasText, _tw, TEXT_SIZES[slide.canvasTextSize], slide)
          const leading = p.textLeading()
          if (_vertCenter) {
            const startY = Math.max(_areaTop, (_areaTop + _areaBottom) / 2 - (lines.length * leading) / 2)
            p.textAlign(_hAlign, p.TOP)
            lines.forEach((line, i) => p.text(line, xBase, startY + i * leading))
          } else if (!_swapped) {
            p.textAlign(_hAlign, p.BOTTOM)
            lines.forEach((line, i) => p.text(line, xBase, p.height - _m - (lines.length - 1 - i) * leading))
          } else {
            p.textAlign(_hAlign, p.TOP)
            lines.forEach((line, i) => p.text(line, xBase, _m + i * leading))
          }
        } else {
          if (_vertCenter) {
            const lineCount = measureWrappedLines(p, slide.canvasText, _tw)
            const startY = Math.max(_areaTop, (_areaTop + _areaBottom) / 2 - (lineCount * p.textLeading()) / 2)
            p.textAlign(_hAlign, p.TOP)
            p.text(slide.canvasText, _mh, startY, _tw)
          } else if (!_swapped) {
            p.textAlign(_hAlign, p.BOTTOM)
            p.text(slide.canvasText, _mh, _m, _tw, p.height - 2 * _m)
          } else {
            p.textAlign(_hAlign, p.TOP)
            p.text(slide.canvasText, _mh, _m, _tw)
          }
        }
      }
    }

  }
}

// ── Übersichts-Modus ───────────────────────────────────────────────────────

const updateOverviewLayout = () => {
  const strip = document.getElementById('slideStrip')
  if (!strip || !overviewMode) return
  const canvasEls = [...strip.querySelectorAll('.slide-item canvas')]
  if (!canvasEls.length) return
  canvasEls.forEach(c => { c.style.zoom = '' })
  void strip.offsetWidth
  const style = getComputedStyle(strip)
  const padH = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)
  const padV = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
  const gap = parseFloat(style.gap) || 0
  const availW = strip.clientWidth - padH - gap * (canvasEls.length - 1)
  const availH = strip.clientHeight - padV
  const rects = canvasEls.map(c => c.getBoundingClientRect())
  const totalW = rects.reduce((sum, r) => sum + r.width, 0)
  const maxH = Math.max(...rects.map(r => r.height))
  if (!totalW || !maxH) return
  const scale = Math.min(availW / totalW, availH / maxH, 1)
  canvasEls.forEach(c => { c.style.zoom = String(scale) })
}

const enterOverviewMode = () => {
  overviewMode = true
  const strip = document.getElementById('slideStrip')
  if (strip) strip.classList.add('overview-mode')
  requestAnimationFrame(() => requestAnimationFrame(updateOverviewLayout))
  const btn = document.getElementById('overviewToggleButton')
  if (btn) { btn.setAttribute('aria-label', 'Übersicht schließen'); btn.dataset.tooltip = 'Übersicht schließen' }
}

const exitOverviewMode = () => {
  overviewMode = false
  const strip = document.getElementById('slideStrip')
  if (strip) {
    strip.classList.remove('overview-mode')
    strip.querySelectorAll('.slide-item canvas').forEach(c => { c.style.zoom = '' })
  }
  const btn = document.getElementById('overviewToggleButton')
  if (btn) { btn.setAttribute('aria-label', 'Alle Slides anzeigen'); btn.dataset.tooltip = 'Alle Slides anzeigen' }
}

// ── Slide-Verwaltung ───────────────────────────────────────────────────────

const makeAddButton = (insertAtIndex) => {
  const btn = mk('button', { type: 'button', innerHTML: slideAddIcon, title: 'Slide hinzufügen', 'aria-label': 'Slide hinzufügen' })
  btn.className = 'slide-add-btn'
  btn.addEventListener('click', () => addSlide(insertAtIndex))
  return btn
}

const renderSlideStrip = () => {
  const strip = document.getElementById('slideStrip')
  if (!strip) return
  strip.innerHTML = ''

  strip.appendChild(makeAddButton(0))

  slides.forEach((slide, i) => {
    if (!slide.canvasContainer) {
      slide.canvasContainer = mk('div')
      slide.canvasContainer.className = 'slide-canvas-wrapper'
      slide.canvasContainer.appendChild(mk('div', { className: 'story-zone-overlay' }))
    }
    updateStoryZoneOverlay(slide)

    const item = mk('div')
    item.className = 'slide-item' + (i === activeSlideIndex ? ' is-active' : '')
    item.dataset.slideIndex = String(i)
    item.draggable = true
    item.addEventListener('click', (e) => {
      if (!e.target.closest('.slide-actions')) {
        if (overviewMode) exitOverviewMode()
        setActiveSlide(i)
      }
    })
    item.addEventListener('dragstart', (e) => {
      if (e.target.closest('.slide-actions')) { e.preventDefault(); return }
      draggedSlideIndex = i
      item.classList.add('is-dragging')
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(i))
    })
    item.addEventListener('dragend', () => {
      draggedSlideIndex = null
      strip.querySelectorAll('.slide-item').forEach(el => {
        el.classList.remove('is-dragging', 'drag-over-left', 'drag-over-right')
      })
    })
    item.addEventListener('dragover', (e) => {
      if (draggedSlideIndex === null) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      if (i === draggedSlideIndex) return
      const before = e.clientX - item.getBoundingClientRect().left < item.offsetWidth / 2
      strip.querySelectorAll('.slide-item').forEach(el => el.classList.remove('drag-over-left', 'drag-over-right'))
      item.classList.toggle('drag-over-left', before)
      item.classList.toggle('drag-over-right', !before)
    })
    item.addEventListener('drop', (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (draggedSlideIndex === null) return
      const before = e.clientX - item.getBoundingClientRect().left < item.offsetWidth / 2
      let target = before ? i : i + 1
      if (target > draggedSlideIndex) target -= 1
      moveSlide(draggedSlideIndex, target)
    })
    item.appendChild(slide.canvasContainer)

    if (!slide.p5Instance) {
      slide.p5Instance = new p5(createSketch(slide))
    }

    const actions = mk('div')
    actions.className = 'slide-actions'

    const genBtn = mk('button', { type: 'button', innerHTML: generateIcon })
    genBtn.className = 'slide-action-btn slide-gen-btn'
    genBtn.dataset.tooltip = `Slide ${i + 1} generieren`
    if (i === activeSlideIndex) genBtn.dataset.shortcut = '⇧R'
    genBtn.disabled = slide.slideLocked
    genBtn.addEventListener('click', () => randomizeSlide(slide))

    const lockSlideBtn = mk('button', { type: 'button' })
    lockSlideBtn.className = 'slide-action-btn' + (slide.slideLocked ? ' is-locked' : '')
    lockSlideBtn.innerHTML = slide.slideLocked ? LOCK_CLOSED_SVG : LOCK_OPEN_SVG
    lockSlideBtn.title = slide.slideLocked ? `Slide ${i + 1} entsperren` : `Slide ${i + 1} sperren`
    lockSlideBtn.addEventListener('click', () => {
      slide.slideLocked = !slide.slideLocked
      lockSlideBtn.innerHTML = slide.slideLocked ? LOCK_CLOSED_SVG : LOCK_OPEN_SVG
      lockSlideBtn.classList.toggle('is-locked', slide.slideLocked)
      lockSlideBtn.title = slide.slideLocked ? `Slide ${i + 1} entsperren` : `Slide ${i + 1} sperren`
      genBtn.disabled = slide.slideLocked
      saveState()
    })

    const editBtn = mk('button', { type: 'button', innerHTML: slideEditIcon, title: 'Slide bearbeiten', 'aria-label': 'Slide bearbeiten' })
    editBtn.className = 'slide-action-btn'
    editBtn.addEventListener('click', () => {
      setActiveSlide(i)
      const editor = document.querySelector('.editor')
      if (editor?.classList.contains('sidebar-hidden')) {
        editor.classList.remove('sidebar-hidden')
        const btn = document.getElementById('sidebarToggleButton')
        if (btn) { btn.setAttribute('aria-label', 'Seitenleiste ausblenden'); btn.dataset.tooltip = 'Seitenleiste ausblenden' }
      }
    })

    const deleteBtn = mk('button', { type: 'button', innerHTML: slideDeleteIcon, title: 'Slide löschen', 'aria-label': 'Slide löschen' })
    deleteBtn.className = 'slide-action-btn slide-action-btn--delete'
    if (slides.length <= 1) deleteBtn.disabled = true
    deleteBtn.addEventListener('click', () => deleteSlide(i))

    const copyBtn = mk('button', { type: 'button', innerHTML: slideCopyIcon, title: `Slide ${i + 1} duplizieren` })
    copyBtn.className = 'slide-action-btn'
    copyBtn.addEventListener('click', () => copySlide(i))

    const exportSlideBtn = mk('button', { type: 'button', innerHTML: exportIcon, title: `Slide ${i + 1} exportieren` })
    exportSlideBtn.className = 'slide-action-btn'
    exportSlideBtn.addEventListener('click', () => {
      slide.p5Instance?.save(buildFilename(slide, i + 1))
    })

    actions.appendChild(genBtn)
    actions.appendChild(lockSlideBtn)
    actions.appendChild(editBtn)
    actions.appendChild(copyBtn)
    actions.appendChild(exportSlideBtn)
    actions.appendChild(deleteBtn)
    item.appendChild(actions)

    strip.appendChild(item)
    strip.appendChild(makeAddButton(i + 1))
  })

  strip.addEventListener('mouseenter', () => strip.classList.add('is-hovered'))
  strip.addEventListener('mouseleave', () => strip.classList.remove('is-hovered'))
  if (overviewMode) requestAnimationFrame(() => requestAnimationFrame(updateOverviewLayout))
}

const renderSlideDropdown = () => {
  const sel = document.getElementById('slideSelect')
  if (!sel) return
  sel.innerHTML = ''
  slides.forEach((_, i) => {
    const opt = mk('option', { value: String(i), textContent: `Slide ${i + 1}` })
    if (i === activeSlideIndex) opt.selected = true
    sel.appendChild(opt)
  })
}

const updateStoryZoneVisibility = (slide) => {
  const row = document.getElementById('storyZoneRow')
  if (row) row.style.display = (slide.canvasFormat === 'breitbild' && slide.canvasOrientation === 'portrait') ? '' : 'none'
}

const updateStoryZoneOverlay = (slide) => {
  const overlay = slide.canvasContainer?.querySelector('.story-zone-overlay')
  if (overlay) overlay.classList.toggle('is-visible', Boolean(slide.storyZone && slide.canvasFormat === 'breitbild' && slide.canvasOrientation === 'portrait'))
}

const syncLockButtons = (slide) => {
  document.querySelectorAll('.lock-btn').forEach(btn => {
    const section = btn.dataset.canvasLock
    const isLocked = slide.lockedSections?.[section] ?? false
    btn.innerHTML = isLocked ? LOCK_CLOSED_SVG : LOCK_OPEN_SVG
    btn.classList.toggle('is-locked', isLocked)
    btn.setAttribute('aria-label', isLocked ? 'Abschnitt entsperren' : 'Abschnitt sperren')
  })
}

const syncSidebarToSlide = (slide) => {
  const textInput = document.getElementById('canvasText')
  const subtitleInput = document.getElementById('canvasSubtitle')
  if (textInput) textInput.value = slide.canvasText
  if (subtitleInput) subtitleInput.value = slide.canvasSubtitle

  updateLogoCheckState(slide)

  document.querySelectorAll('.text-size-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.textSize === slide.canvasTextSize)
  })

  document.querySelectorAll('.text-align-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.textAlign === slide.canvasTextAlign)
  })

  const hyphenCheck = document.getElementById('hyphenCheck')
  if (hyphenCheck) hyphenCheck.checked = slide.canvasTextHyphens

  const swapTextsCheck = document.getElementById('swapTextsCheck')
  if (swapTextsCheck) swapTextsCheck.checked = slide.textsSwapped

  const storyZoneCheck = document.getElementById('storyZoneCheck')
  if (storyZoneCheck) storyZoneCheck.checked = slide.storyZone
  updateStoryZoneVisibility(slide)


  const patternClampSidesCheck = document.getElementById('patternClampSidesCheck')
  if (patternClampSidesCheck) patternClampSidesCheck.checked = slide.patternClampSides

  const formatSelect = document.getElementById('canvasFormatSelect')
  if (formatSelect) formatSelect.value = slide.canvasFormat

  document.querySelectorAll('.orientation-btn').forEach(btn => {
    if (btn.dataset.orientation) {
      btn.classList.toggle('active', btn.dataset.orientation === slide.canvasOrientation)
    }
  })

  renderColorSection(slide)
  renderCanvasImageControls(slide)

  const contextContainer = document.getElementById('contextControls')
  if (contextContainer) {
    contextContainer.innerHTML = ''
    contextRenderers[slide.activeContextId](contextContainer, slide.contextState[slide.activeContextId], slide)
  }

  syncVisibilityButtons(slide)
  syncLockButtons(slide)
  renderSlideDropdown()
}

const setActiveSlide = (index) => {
  if (index === activeSlideIndex && index < slides.length) {
    // Already active, just make sure visual is updated
  } else {
    activeSlideIndex = Math.max(0, Math.min(index, slides.length - 1))
  }
  syncSidebarToSlide(getActive())
  document.querySelectorAll('.slide-item').forEach((el, i) => {
    el.classList.toggle('is-active', i === activeSlideIndex)
    // ⇧R generiert nur den aktiven Slide — Hinweis wandert mit
    const genBtn = el.querySelector('.slide-gen-btn')
    if (genBtn) {
      if (i === activeSlideIndex) genBtn.dataset.shortcut = '⇧R'
      else delete genBtn.dataset.shortcut
    }
  })
  const activeItem = document.querySelector('.slide-item.is-active')
  if (activeItem) activeItem.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
}

const updateExportBtnCount = () => {
  const el = document.getElementById('exportSlideCount')
  if (!el) return
  el.textContent = slides.length === 1 ? '1 Slide' : `${slides.length} Slides`
}

const updateGenerateBtnCount = () => {
  const el = document.getElementById('generateSlideCount')
  if (!el) return
  el.textContent = slides.length === 1 ? '1 Slide' : `alle ${slides.length} Slides`
}

const randomizeSlide = (slide, preserveSchemeAndFormat = false) => {
  if (slide.slideLocked) return

  const locked = slide.lockedSections ?? {}

  // ── Format & Orientierung ──────────────────────────────────────────────
  if (!preserveSchemeAndFormat && !locked.format) {
    slide.canvasFormat = randPick(Object.keys(CANVAS_FORMATS))
    slide.canvasOrientation = randPick(['portrait', 'landscape'])
  }

  // ── Texteigenschaften ────────────────────────────────────────────────
  if (!locked.text) {
    slide.canvasTextSize = randPick(Object.keys(TEXT_SIZES))
    slide.canvasTextAlign = randPick(['left', 'center'])
    slide.textsSwapped = Math.random() < 0.5
    slide.showLogo = Math.random() < 0.5
  }
  updateLogoImage(slide)

  // ── Inhaltserkennung ──────────────────────────────────────────────────
  // Ob der Slide sichtbaren Text hat (Inhalt vorhanden UND nicht ausgeblendet)
  const hasText = slide.showText && (hasContent(slide.canvasText) || hasContent(slide.canvasSubtitle))
  // Ob ein Foto geladen oder aus dem Pool wählbar ist
  const canUseImage = fotografieUrls.length > 0 || !!slide.canvasImage

  // ── Hilfsfunktionen ───────────────────────────────────────────────────

  // Setzt ein zufälliges Farbpaar aus Schema 2 (übersprungen wenn Farben gesperrt)
  const applyRandomColorScheme = () => {
    if (locked.background) return
    slide.activeColorSchemeId = 'schema2'
    const scheme = getActiveScheme(slide)
    slide.activePairIndex = randInt(0, scheme.pairs.length - 1)
    const pair = getActivePair(slide)
    slide.canvasBgColor = pair.bg
    slide.canvasTextColor = pair.text
    slide.colorDuoton = Math.random() < 0.5
  }

  // Aktiviert Studie 2 als Muster-Kontext mit zufälliger Spaltenanzahl (übersprungen wenn Muster gesperrt)
  const applyStudie2Pattern = () => {
    if (locked.pattern) return
    slide.activeContextId = 'studie2'
    slide.showPattern = true
    const s2 = slide.contextState['studie2']
    const isLandscape = slide.canvasFormat === 'breitbild' && slide.canvasOrientation === 'landscape'
    s2.options.columns = isLandscape ? randInt(8, 24) : randInt(3, 8)
    s2.options.blur = false
    loadStudie2Pattern(s2, slide)
  }

  // ── Szenarien (abhängig vom vorhandenen Inhalt) ────────────────────────

  if (hasText && !canUseImage) {
    // Szenario 1: Nur Text – Muster aktivieren, kein Bild
    if (!locked.image) slide.showImage = false
    applyRandomColorScheme()
    applyStudie2Pattern()

  } else if (hasText && canUseImage) {
    // Szenario 2: Text + Bild – 50/50-Chance zwischen zwei Varianten
    if (!locked.image) {
      pickRandomFotografie(slide)
      slide.showImage = true
    }
    applyRandomColorScheme()

    if (Math.random() < 0.5) {
      // Option A: Muster aktivieren, Bild als größte Form eingebettet
      if (!locked.image) slide.canvasImageMode = 'pattern'
      applyStudie2Pattern()
    } else {
      // Option B: Kein Muster, Bild mit zufälliger Maske
      if (!locked.pattern) slide.showPattern = false
      if (!locked.image) {
        slide.canvasImageMode = 'mask'
        slide.canvasImageScale = randInt(40, 100)
        slide.canvasImageRotation = randInt(-8, 8)
        pickRandomMask(slide)
      }
    }

  } else if (!hasText && canUseImage) {
    // Szenario 3: Nur Bild (auch wenn Text vorhanden aber ausgeblendet)
    // 50/50-Chance zwischen zwei Varianten
    if (!locked.image) {
      pickRandomFotografie(slide)
      slide.showImage = true
    }
    applyRandomColorScheme()

    if (Math.random() < 0.5) {
      // Option A: Bild als größte Form im Muster eingebettet
      if (!locked.image) slide.canvasImageMode = 'pattern'
      applyStudie2Pattern()
    } else {
      // Option B: Vollflächig im Hintergrund, Muster darüber
      if (!locked.image) {
        slide.canvasImageMode = 'background'
        slide.canvasBgMenge = randInt(10, 30)
      }
      applyStudie2Pattern()
    }

  } else {
    // Fallback: kein Text, kein Bild – nur Muster
    if (!locked.image) slide.showImage = false
    applyRandomColorScheme()
    applyStudie2Pattern()
  }

  syncVisibilityButtons(slide)

  const state = slide.contextState[slide.activeContextId]

  if (slide !== getActive()) {
    const [w, h] = CANVAS_FORMATS[slide.canvasFormat][slide.canvasOrientation]
    slide.p5Instance.resizeCanvas(w, h)
    updateLogoCheckState(slide)
    if (state.images.length) applyPlacement(state, slide)
    updateStoryZoneOverlay(slide)
    saveState()
    return
  }

  // ── Sidebar für aktives Slide synchronisieren ──────────────────────────
  const formatSelect = document.getElementById('canvasFormatSelect')
  if (formatSelect) formatSelect.value = slide.canvasFormat
  document.querySelectorAll('.orientation-btn').forEach(btn => { if (btn.dataset.orientation) btn.classList.toggle('active', btn.dataset.orientation === slide.canvasOrientation) })
  document.querySelectorAll('.text-size-btn').forEach(btn => btn.classList.toggle('active', btn.dataset.textSize === slide.canvasTextSize))
  document.querySelectorAll('.text-align-btn').forEach(btn => btn.classList.toggle('active', btn.dataset.textAlign === slide.canvasTextAlign))
  const hyphenCheck = document.getElementById('hyphenCheck')
  if (hyphenCheck) hyphenCheck.checked = slide.canvasTextHyphens

  const [w, h] = CANVAS_FORMATS[slide.canvasFormat][slide.canvasOrientation]
  slide.p5Instance.resizeCanvas(w, h)
  updateLogoCheckState(slide)
  if (state.images.length) applyPlacement(state, slide)
  renderColorSection(slide)
  renderCanvasImageControls(slide)
  syncVisibilityButtons(slide)
  updateStoryZoneVisibility(slide)
  updateStoryZoneOverlay(slide)

  const contextContainer = document.getElementById('contextControls')
  contextContainer.innerHTML = ''
  contextRenderers[slide.activeContextId](contextContainer, slide.contextState[slide.activeContextId], slide)
  saveState()
}

const copySlide = (sourceIndex) => {
  const source = slides[sourceIndex]
  const newSlide = defaultSlideState()
  const freshId = newSlide.slideId
  applySlideData(newSlide, serializeSlide(source))
  // Kopie braucht eine eigene slideId, sonst teilen sich beide Slides
  // denselben IndexedDB-Eintrag (Löschen/Upload träfe auch das Original)
  newSlide.slideId = freshId
  idbGetImage(source.slideId).then((blob) => { if (blob) idbPutImage(freshId, blob) })
  newSlide.canvasImageUrl = source.canvasImageUrl
  newSlide.canvasImageMaskUrl = source.canvasImageMaskUrl
  const insertAt = sourceIndex + 1
  slides.splice(insertAt, 0, newSlide)
  activeSlideIndex = insertAt
  renderSlideStrip()
  updateExportBtnCount()
  updateGenerateBtnCount()
  syncSidebarToSlide(getActive())
  saveState()
}

const addSlide = (insertAtIndex) => {
  const refSlide = slides[insertAtIndex - 1] ?? slides[insertAtIndex] ?? slides[0]
  const newSlide = defaultSlideState()
  if (refSlide) {
    newSlide.canvasFormat = refSlide.canvasFormat
    newSlide.canvasOrientation = refSlide.canvasOrientation
  }
  newSlide._needsRandomize = true
  slides.splice(insertAtIndex, 0, newSlide)
  activeSlideIndex = insertAtIndex
  renderSlideStrip()
  updateExportBtnCount()
  updateGenerateBtnCount()
  syncSidebarToSlide(getActive())
  saveState()
}

const moveSlide = (fromIndex, toIndex) => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= slides.length || toIndex >= slides.length) return
  const [slide] = slides.splice(fromIndex, 1)
  slides.splice(toIndex, 0, slide)
  if (activeSlideIndex === fromIndex) activeSlideIndex = toIndex
  else if (fromIndex < activeSlideIndex && toIndex >= activeSlideIndex) activeSlideIndex--
  else if (fromIndex > activeSlideIndex && toIndex <= activeSlideIndex) activeSlideIndex++
  renderSlideStrip()
  renderSlideDropdown()
  saveState()
}

const deleteSlide = (index) => {
  if (slides.length <= 1) return
  const slide = slides[index]
  slide.p5Instance?.remove()
  if (slide.canvasImageUrl) URL.revokeObjectURL(slide.canvasImageUrl)
  idbDeleteImage(slide.slideId)
  if (slide.canvasImageMaskUrl) URL.revokeObjectURL(slide.canvasImageMaskUrl)
  for (const state of Object.values(slide.contextState)) {
    for (const form of state.forms) { if (form.gfx) form.gfx.remove() }
  }
  slide.bgCornerGrid.forEach(f => { if (f.gfx) f.gfx.remove() })
  slides.splice(index, 1)
  activeSlideIndex = Math.min(activeSlideIndex, slides.length - 1)
  renderSlideStrip()
  updateExportBtnCount()
  updateGenerateBtnCount()
  syncSidebarToSlide(getActive())
  saveState()
}

// ── AutoPlay ───────────────────────────────────────────────────────────────

let autoPlayTimer = null
let autoPlayRafId = null
let autoPlayStart = null

const updateAutoPlayProgress = (btn) => {
  if (!autoPlayStart) return
  const progress = Math.min((performance.now() - autoPlayStart) / AUTOPLAY_MS * 100, 100)
  btn.style.setProperty('--autoplay-progress', progress + '%')
  autoPlayRafId = requestAnimationFrame(() => updateAutoPlayProgress(btn))
}

const startAutoPlay = (btn, generateBtn) => {
  btn.innerHTML = AUTOPLAY_STOP_SVG
  btn.classList.add('is-playing')
  btn.title = 'Automatisches Generieren stoppen'

  const tick = () => {
    generateBtn.click()
    autoPlayStart = performance.now()
    cancelAnimationFrame(autoPlayRafId)
    updateAutoPlayProgress(btn)
    autoPlayTimer = setTimeout(tick, AUTOPLAY_MS)
  }

  autoPlayStart = performance.now()
  updateAutoPlayProgress(btn)
  autoPlayTimer = setTimeout(tick, AUTOPLAY_MS)
}

const stopAutoPlay = (btn) => {
  clearTimeout(autoPlayTimer)
  cancelAnimationFrame(autoPlayRafId)
  autoPlayTimer = null
  autoPlayStart = null
  autoPlayRafId = null
  btn.innerHTML = AUTOPLAY_PLAY_SVG
  btn.classList.remove('is-playing')
  btn.style.setProperty('--autoplay-progress', '0%')
  btn.title = 'Automatisch generieren'
}

// ── Haupt-Setup ────────────────────────────────────────────────────────────

const startP5 = () => {
  loadTheme()
  loadStoredState()

  const overviewToggleButton = document.getElementById('overviewToggleButton')
  if (overviewToggleButton) {
    overviewToggleButton.innerHTML = overviewIcon
    overviewToggleButton.addEventListener('click', () => {
      if (overviewMode) exitOverviewMode()
      else enterOverviewMode()
    })
  }

  new ResizeObserver(() => { if (overviewMode) updateOverviewLayout() })
    .observe(document.getElementById('slideStrip'))

  const slideStripEl = document.getElementById('slideStrip')
  if (slideStripEl) {
    slideStripEl.addEventListener('dragover', (e) => {
      if (draggedSlideIndex === null) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
    })
    slideStripEl.addEventListener('drop', (e) => {
      if (draggedSlideIndex === null || e.target.closest('.slide-item')) return
      e.preventDefault()
      moveSlide(draggedSlideIndex, slides.length - 1)
    })
  }

  const sidebarToggleButton = document.getElementById('sidebarToggleButton')
  const settingsFabButton = document.getElementById('settingsFabButton')
  const settingsBackdrop = document.getElementById('settingsBackdrop')
  const asideEl = document.querySelector('aside')
  const isMobileLayout = () => window.matchMedia('(max-width: 1100px)').matches

  function openMobileAside() {
    asideEl?.classList.add('is-open')
    settingsBackdrop?.classList.add('is-visible')
  }
  function closeMobileAside() {
    asideEl?.classList.remove('is-open')
    settingsBackdrop?.classList.remove('is-visible')
  }

  if (sidebarToggleButton) {
    sidebarToggleButton.innerHTML = sidebarToggleIcon
    const editor = document.querySelector('.editor')
    sidebarToggleButton.addEventListener('click', () => {
      if (isMobileLayout()) {
        openMobileAside()
      } else {
        const hidden = editor.classList.toggle('sidebar-hidden')
        sidebarToggleButton.setAttribute('aria-label', hidden ? 'Seitenleiste einblenden' : 'Seitenleiste ausblenden')
        sidebarToggleButton.dataset.tooltip = hidden ? 'Seitenleiste einblenden' : 'Seitenleiste ausblenden'
      }
    })
  }

  settingsFabButton?.addEventListener('click', openMobileAside)
  settingsBackdrop?.addEventListener('click', closeMobileAside)

  const themeToggleButton = document.getElementById('themeToggleButton')
  if (themeToggleButton) {
    themeToggleButton.addEventListener('click', () => {
      applyTheme(currentTheme === 'dark' ? 'light' : 'dark')
    })
  }
  applyTheme(currentTheme)

  const autoPlayButton = document.getElementById('autoPlayButton')
  if (autoPlayButton) {
    const generateBtn = document.getElementById('generateBtn')
    autoPlayButton.innerHTML = AUTOPLAY_PLAY_SVG
    autoPlayButton.addEventListener('click', () => {
      if (autoPlayTimer) stopAutoPlay(autoPlayButton)
      else startAutoPlay(autoPlayButton, generateBtn)
    })
  }

  // ── Sidebar-Event-Listener (always operate on getActive()) ─────────────

  const refreshPatternForTextChange = (slide) => {
    syncVisibilityButtons(slide)
    if (slide.canvasImageMode === 'background') renderCanvasImageControls(slide)
  }

  const textInput = document.getElementById('canvasText')
  textInput.addEventListener('input', () => {
    getActive().canvasText = textInput.value
    refreshPatternForTextChange(getActive())
    applyPlacement(getActive().contextState[getActive().activeContextId], getActive())
  })

  const subtitleInput = document.getElementById('canvasSubtitle')
  subtitleInput.addEventListener('input', () => {
    getActive().canvasSubtitle = subtitleInput.value
    updateLogoCheckState(getActive())
    refreshPatternForTextChange(getActive())
    applyPlacement(getActive().contextState[getActive().activeContextId], getActive())
  })

  const swapTextsCheck = document.getElementById('swapTextsCheck')
  if (swapTextsCheck) {
    swapTextsCheck.addEventListener('change', () => {
      const slide = getActive()
      slide.textsSwapped = swapTextsCheck.checked
      saveState()
      refreshPatternForTextChange(slide)
      applyPlacement(slide.contextState[slide.activeContextId], slide)
      if (slide.canvasReady) slide.p5Instance.redraw()
    })
  }

  const storyZoneCheck = document.getElementById('storyZoneCheck')
  if (storyZoneCheck) {
    storyZoneCheck.addEventListener('change', () => {
      const slide = getActive()
      slide.storyZone = storyZoneCheck.checked
      updateStoryZoneOverlay(slide)
      const state = slide.contextState[slide.activeContextId]
      if (state.images.length) applyPlacement(state, slide)
      else if (slide.canvasReady) slide.p5Instance.redraw()
      saveState()
    })
  }

  const patternClampSidesCheck = document.getElementById('patternClampSidesCheck')
  if (patternClampSidesCheck) {
    patternClampSidesCheck.addEventListener('change', () => {
      getActive().patternClampSides = patternClampSidesCheck.checked
      saveState()
      applyPlacement(getActive().contextState[getActive().activeContextId], getActive())
    })
  }

  const textSizeBtns = document.querySelectorAll('.text-size-btn')
  textSizeBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      getActive().canvasTextSize = btn.dataset.textSize
      textSizeBtns.forEach(b => b.classList.toggle('active', b === btn))
      saveState()
      applyPlacement(getActive().contextState[getActive().activeContextId], getActive())
    })
  })

  const textAlignBtns = document.querySelectorAll('.text-align-btn')
  textAlignBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      getActive().canvasTextAlign = btn.dataset.textAlign
      textAlignBtns.forEach(b => b.classList.toggle('active', b === btn))
      saveState()
      if (getActive().canvasReady) getActive().p5Instance.redraw()
    })
  })

  const hyphenCheck = document.getElementById('hyphenCheck')
  if (hyphenCheck) {
    hyphenCheck.addEventListener('change', () => {
      getActive().canvasTextHyphens = hyphenCheck.checked
      _hyphenCache.key = null
      saveState()
      applyPlacement(getActive().contextState[getActive().activeContextId], getActive())
    })
  }


  const logoCheck = document.getElementById('logoCheck')
  if (logoCheck) {
    logoCheck.addEventListener('change', () => {
      getActive().showLogo = logoCheck.checked
      saveState()
      if (logoCheck.checked) updateLogoImage(getActive())
      else { getActive().canvasLogoImage = null; if (getActive().canvasReady) getActive().p5Instance.redraw() }
    })
  }

  document.querySelectorAll('.visibility-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation()
      const slide = getActive()
      const toggle = btn.dataset.canvasToggle
      if (toggle === 'text') slide.showText = !slide.showText
      else if (toggle === 'image') slide.showImage = !slide.showImage
      else if (toggle === 'background') slide.showBackground = !slide.showBackground
      else if (toggle === 'pattern') slide.showPattern = !slide.showPattern
      syncVisibilityButtons(slide)
      if (slide.canvasImageMode === 'background') renderCanvasImageControls(slide)
      if (slide.canvasReady && toggle !== 'format') {
        if (toggle === 'text') applyPlacement(slide.contextState[slide.activeContextId], slide)
        else slide.p5Instance.redraw()
      }
    })
  })

  document.querySelectorAll('.lock-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation()
      const slide = getActive()
      const section = btn.dataset.canvasLock
      if (!slide.lockedSections) slide.lockedSections = {}
      slide.lockedSections[section] = !slide.lockedSections[section]
      btn.innerHTML = slide.lockedSections[section] ? LOCK_CLOSED_SVG : LOCK_OPEN_SVG
      btn.classList.toggle('is-locked', slide.lockedSections[section])
      btn.setAttribute('aria-label', slide.lockedSections[section] ? 'Abschnitt entsperren' : 'Abschnitt sperren')
      saveState()
    })
  })

  const canvasImageUpload = document.getElementById('canvasImageUpload')
  const uploadDropzone = document.getElementById('uploadDropzone')
  if (uploadDropzone && canvasImageUpload) {
    uploadDropzone.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); canvasImageUpload.click() }
    })
    ;['dragenter', 'dragover'].forEach(ev => uploadDropzone.addEventListener(ev, (e) => {
      e.preventDefault(); uploadDropzone.classList.add('is-dragover')
    }))
    ;['dragleave', 'drop'].forEach(ev => uploadDropzone.addEventListener(ev, () => {
      uploadDropzone.classList.remove('is-dragover')
    }))
    uploadDropzone.addEventListener('drop', (e) => {
      e.preventDefault()
      const file = e.dataTransfer?.files?.[0]
      if (!file || !file.type.startsWith('image/')) return
      const dt = new DataTransfer(); dt.items.add(file)
      canvasImageUpload.files = dt.files
      canvasImageUpload.dispatchEvent(new Event('change'))
    })
  }
  if (canvasImageUpload) {
    canvasImageUpload.addEventListener('change', () => {
      const file = canvasImageUpload.files?.[0]
      if (!file) return
      const slide = getActive()
      const nextUrl = URL.createObjectURL(file)
      slide.p5Instance.loadImage(nextUrl, (img) => {
        if (slide.canvasImageUrl) URL.revokeObjectURL(slide.canvasImageUrl)
        slide.canvasImageUrl = nextUrl
        slide.canvasImage = img
        idbPutImage(slide.slideId, file)
        pickRandomMask(slide)
        saveState()
        renderCanvasImageControls(slide)
        if (slide.canvasReady) slide.p5Instance.redraw()
      }, () => {
        URL.revokeObjectURL(nextUrl)
        slide.canvasImage = null
        slide.canvasImageUrl = null
        slide.canvasImageMask = null
        slide.canvasImageMaskUrl = null
        renderCanvasImageControls(slide)
      })
    })
  }

  const generateBtn = document.getElementById('generateBtn')
  generateBtn.innerHTML = `${generateIcon}<span class="btn-generate-label"><span>Generieren</span><span id="generateSlideCount"></span></span>`

  const exportBtn = document.getElementById('exportBtn')
  exportBtn.innerHTML = `${exportIcon}<span class="btn-export-label"><span>Exportieren</span><span id="exportSlideCount"></span></span>`
  exportBtn.addEventListener('click', () => {
    slides.forEach((slide, i) => {
      setTimeout(() => {
        slide.p5Instance?.save(buildFilename(slide, i + 1))
      }, i * 200)
    })
  })

  const formatSelect = document.getElementById('canvasFormatSelect')
  const orientationBtns = document.querySelectorAll('.orientation-btn')

  const applyCanvasFormat = () => {
    const slide = getActive()
    slide.canvasFormat = formatSelect.value
    const [w, h] = CANVAS_FORMATS[slide.canvasFormat][slide.canvasOrientation]
    slide.p5Instance.resizeCanvas(w, h)
    updateLogoCheckState(slide)
    for (const state of Object.values(slide.contextState)) {
      if (state.images.length) applyPlacement(state, slide)
    }
    const contextContainer = document.getElementById('contextControls')
    if (contextContainer) {
      contextContainer.innerHTML = ''
      contextRenderers[slide.activeContextId](contextContainer, slide.contextState[slide.activeContextId], slide)
    }
    updateStoryZoneVisibility(slide)
    updateStoryZoneOverlay(slide)
    if (slide.canvasImageMode === 'background') renderCanvasImageControls(slide)
    saveState()
  }

  formatSelect.addEventListener('change', applyCanvasFormat)
  orientationBtns.forEach(btn => {
    if (!btn.dataset.orientation) return
    btn.addEventListener('click', () => {
      getActive().canvasOrientation = btn.dataset.orientation
      orientationBtns.forEach(b => { if (b.dataset.orientation) b.classList.toggle('active', b === btn) })
      applyCanvasFormat()
    })
  })

  // Slide dropdown
  const slideSelect = document.getElementById('slideSelect')
  if (slideSelect) {
    slideSelect.addEventListener('change', () => {
      setActiveSlide(parseInt(slideSelect.value, 10))
    })
  }

  generateBtn.addEventListener('click', () => {
    if (slides.length > 1) {
      slides.forEach(s => randomizeSlide(s, true))
    } else {
      randomizeSlide(getActive())
    }
  })

  document.addEventListener('keydown', (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return
    const tag = document.activeElement?.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

    // Shift+R: nur den aktiven Slide neu generieren
    if (e.key === 'R' && e.shiftKey) { randomizeSlide(getActive()); return }
    if (e.shiftKey) return

    if (e.key === 'r') generateBtn.click()
    if (e.key === 'f') overviewMode ? exitOverviewMode() : enterOverviewMode()
    if (e.key === 'd') applyTheme(currentTheme === 'dark' ? 'light' : 'dark')
    if (e.key === 's') { if (isMobileLayout()) openMobileAside(); else sidebarToggleButton?.click() }
    if (e.key === 'Escape') { if (isMobileLayout()) closeMobileAside() }
    if (e.key === 'p' || e.key === ' ') { e.preventDefault(); autoPlayButton?.click() }
  })

  // ── Slide-Strip aufbauen ──────────────────────────────────────────────

  p5.disableFriendlyErrors = true

  initTooltips()
  renderSlideStrip()
  if (!restoredFromStorage) slides.forEach(s => randomizeSlide(s, true))
  updateExportBtnCount()
  updateGenerateBtnCount()
  syncSidebarToSlide(getActive())

  // ── Fonts + Test-Bild ─────────────────────────────────────────────────

  document.fonts.ready.then(() => {
    _hyphenCache.key = null
    for (const slide of slides) {
      updateLogoCheckState(slide)
      if (slide.canvasReady) {
        // p5 cached die Font-Auflösung: wurde „Social" vor dem Font-Load
        // aufgelöst, bliebe sonst dauerhaft die alte Rasterung aktiv
        slide.p5Instance.textFont('Social')
        slide.p5Instance.redraw()
      }
    }
  })

  // Bilder ohne persistente URL wiederherstellen: Upload aus IndexedDB,
  // sonst Test-Bild als Dev-Fallback
  for (const slide of slides) {
    if (slide.canvasImageUrl) continue
    idbGetImage(slide.slideId).then((blob) => {
      if (slide.canvasImageUrl) return
      const url = blob ? URL.createObjectURL(blob) : TEST_IMAGE
      if (!url) return
      slide.p5Instance.loadImage(url, (img) => {
        slide.canvasImage = img
        slide.canvasImageUrl = url
        if (!slide.canvasImageMaskUrl) pickRandomMask(slide)
        if (slide === getActive()) renderCanvasImageControls(slide)
        if (slide.canvasReady) slide.p5Instance.redraw()
      })
    })
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startP5, { once: true })
} else {
  startP5()
}
